import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { motion, AnimatePresence } from 'framer-motion';
import Profile from '../comp/Profile';
import ThemeSwitcher from '../comp/ThemeSwitcher';

const Icons = {
  Download: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  ),
  Doodle: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>,
  ZoomIn: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>,
  ZoomOut: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>,
  Pen: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>,
  Eraser: (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="w-6 h-6"
      fill="currentColor"
    >
      <g strokeWidth="0"></g>
      <g strokeLinecap="round" strokeLinejoin="round"></g>
      <g>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3.49997 12.8995C2.71892 13.6805 2.71892 14.9468 3.49997 15.7279L7.35785 19.5858H4.08576C3.53347 19.5858 3.08576 20.0335 3.08576 20.5858C3.08576 21.1381 3.53347 21.5858 4.08576 21.5858H20.0858C20.638 21.5858 21.0858 21.1381 21.0858 20.5858C21.0858 20.0335 20.638 19.5858 20.0858 19.5858H10.9558L20.4705 10.071C21.2516 9.28999 21.2516 8.02366 20.4705 7.24261L16.2279 2.99997C15.4468 2.21892 14.1805 2.21892 13.3995 2.99997L3.49997 12.8995ZM7.82579 11.4021L4.91418 14.3137L9.15683 18.5563L12.0684 15.6447L7.82579 11.4021ZM9.24 9.98787L13.4826 14.2305L19.0563 8.65683L14.8137 4.41418L9.24 9.98787Z"
        />
      </g>
    </svg>
  ),
  Hand: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" /></svg>,
  Undo: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>,
  Redo: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /></svg>,
  Chat: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
  Send: <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>,
  Close: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>,
  Share: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>,
  Copy: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>,
  Crown: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
  Users: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" /></svg>,
  Settings: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  Lock: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
  Unlock: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>,
  Ban: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>,
  Kick: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>,
  Eye: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
  EyeOff: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>,
  Trash: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
  Check: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>,
  Rect: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>,
  Circle: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /></svg>,
  Pentagon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2L21.5 8.9L17.9 20.1H6.1L2.5 8.9Z" /></svg>,
  Line45: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="4" y1="20" x2="20" y2="4" /></svg>,
  Arrow45: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 19L19 5M13 5H19V11" /></svg>, Text: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 7V4h16v3M9 20h6M12 4v16" /></svg>,
  Select: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4l7.07 16.97 2.12-7.07 7.07-2.12L4 4z" strokeLinejoin="round" /></svg>,
  Plus: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" /></svg>
};

const COLORS = ['#000000', '#ef4444', '#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6'];
const WIDTHS = [2, 4, 8, 12];
const ERASER_RADIUS = 20;
const BOARD_WIDTH = 1920;
const BOARD_HEIGHT = 1080;

// Optimized Math Helper
function distanceToSegment(p, v, w) {
  const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
  if (l2 === 0) return Math.hypot(p.x - v.x, p.y - v.y);

  let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
  t = Math.max(0, Math.min(1, t));

  const projX = v.x + t * (w.x - v.x);
  const projY = v.y + t * (w.y - v.y);
  return Math.hypot(p.x - projX, p.y - projY);
}

export default function CanvasBoard({ data, setData }) {
  const [msg, setmsg] = useState('hu');
  const { id } = useParams();
  const [name, setName] = useState('');
  const [uname, setUname] = useState('');
  const [view, setView] = useState({ x: 0, y: 0, z: 1 });
  const [strokes, setStrokes] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [cursors, setCursors] = useState({});
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loadingBoard, setLoadingBoard] = useState(true);
  const [overlayMsg, setOverlayMsg] = useState(null);
  const [tool, setTool] = useState('pen');
  const [erasedIds, setErasedIds] = useState(new Set());
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [isSpacePressed] = useState(false);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const currentPathRef = useRef([]);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [isPenMenuOpen, setIsPenMenuOpen] = useState(false);
  const [shapeKind, setShapeKind] = useState('rect');
  const [shapePreview, setShapePreview] = useState(null);
  const [textInputVisible, setTextInputVisible] = useState(false);
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
  const [textValue, setTextValue] = useState('');
  const [textFontSize, setTextFontSize] = useState(28);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const movingRef = useRef(false);
  const moveStartRef = useRef({ x: 0, y: 0 });
  const resizingRef = useRef(null); // { corner: 'tl'|'tr'|'bl'|'br', start: {x,y}, prevMeta }
  const resizeStartMetaRef = useRef(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const chatEndRef = useRef(null);
  const currentGroupId = useRef(null);
  const containerRef = useRef(null);
  const socketRef = useRef(null);
  const connectedRef = useRef(false);
  const localMetaVersionRef = useRef({});
  const panStartRef = useRef({ x: 0, y: 0 });
  const touchDistRef = useRef(null);
  const activePointers = useRef(new Set());
  const erasedIdsRef = useRef(new Set());
  const touchCenterRef = useRef(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadSettings, setDownloadSettings] = useState({
    format: 'png',
    background: 'plain',
    quality: 0.92
  });
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminTab, setAdminTab] = useState('users');
  const [boardSettings, setBoardSettings] = useState({
    isPrivate: false,
    canDraw: true,
    eraseOwnOnly: true,
    maxUsers: 50
  });
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserPermissions, setShowUserPermissions] = useState({});
  const [canUserDraw, setCanUserDraw] = useState(true);
  const [adminMessage, setAdminMessage] = useState('');
  const [fillColor, setFillColor] = useState('transparent');
  const lastEraserUpdateRef = useRef(0); // <--- ADD THIS LINE
  const spatialGridRef = useRef(new Map());
  const CELL_SIZE = 200; // Size of grid chunks

  // Helper to get grid keys for a bounding box
  const getGridKeys = useCallback((minX, minY, maxX, maxY) => {
    const startX = Math.floor(minX / CELL_SIZE);
    const endX = Math.floor(maxX / CELL_SIZE);
    const startY = Math.floor(minY / CELL_SIZE);
    const endY = Math.floor(maxY / CELL_SIZE);

    const keys = [];
    for (let x = startX; x <= endX; x++) {
      for (let y = startY; y <= endY; y++) {
        keys.push(`${x},${y}`);
      }
    }
    return keys;
  }, []);


  useEffect(() => {
    const grid = new Map();

    strokes.forEach(s => {
      if (!s) return;
      const m = s.meta || {};
      // Calculate bounds once per update
      const x0 = m.x0 ?? s.x0 ?? m.x ?? 0;
      const y0 = m.y0 ?? s.y0 ?? m.y ?? 0;
      // For text/images, estimate width/height if not present
      let width = Math.abs((m.x1 ?? s.x1 ?? x0) - x0);
      let height = Math.abs((m.y1 ?? s.y1 ?? y0) - y0);

      // Fix for single point or text items having 0 width
      if (width === 0) width = Math.max(s.width || 0, m.fontSize ? m.text.length * m.fontSize * 0.6 : 20);
      if (height === 0) height = Math.max(s.width || 0, m.fontSize || 20);

      const minX = Math.min(x0, x0 + width) - (s.width || 0);
      const maxX = Math.max(x0, x0 + width) + (s.width || 0);
      const minY = Math.min(y0, y0 + height) - (s.width || 0);
      const maxY = Math.max(y0, y0 + height) + (s.width || 0);

      // Store pre-calculated bounds on the object wrapper for the eraser to use
      const item = { stroke: s, bounds: { minX, maxX, minY, maxY } };

      const keys = getGridKeys(minX, minY, maxX, maxY);
      keys.forEach(key => {
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key).push(item);
      });
    });

    spatialGridRef.current = grid;
  }, [strokes, getGridKeys]);
  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

  useEffect(() => {
    const initialScale = Math.min(window.innerWidth / BOARD_WIDTH, window.innerHeight / BOARD_HEIGHT) * 0.9;
    const initialX = (window.innerWidth - BOARD_WIDTH * initialScale) / 2;
    const initialY = (window.innerHeight - BOARD_HEIGHT * initialScale) / 2;
    setView({ x: initialX, y: initialY, z: initialScale });

    const checkboardno = async () => {
      try {
        setLoadingBoard(true);
        const res = await fetch(`${process.env.REACT_APP_APP_URL}/checkboard`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id }),
        });
        const result = await res.json();

        if (result.req) {
          setIsAdmin(result.isAdmin);
          setBoardSettings(result.settings || {});
          setAllowedUsers(result.allowedUsers || []);
          setBannedUsers(result.bannedUsers || []);
          setCanUserDraw(result.settings?.canDraw !== false);
        } else {
          setOverlayMsg(result.message || 'Board not found');
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        }
      } catch (e) {
        console.error(e);
        setOverlayMsg('Error loading board');
      }
      finally {
        setLoadingBoard(false);
      }
    };
    checkboardno();
  }, [id]);

  useEffect(() => {
    if (!data?.req || connectedRef.current) return;
    const computedName = data.auth ? `${data.user.firstName} ${data.user.lastName}` : data.user.username;
    setUname(computedName);

    socketRef.current = io(process.env.REACT_APP_APP_URL, {
      query: {
        boardId: id,
        name: computedName,
        profile_p: data.user.profile_p,
        userId: data.user.id
      }
    });

    // Join board
    socketRef.current.emit('join-board');

    // Socket event listeners
    socketRef.current.on('stroke', ({ stroke, uname }) => {
      setStrokes(prev => [...prev, stroke]);
      updateCursor(stroke.x1, stroke.y1, uname);
    });
    socketRef.current.on('updateStroke', (payload) => {
      // payload: { updated, clientVersion, groupId }
      const { updated, clientVersion, groupId } = payload || {};
      if (!updated || !groupId) return;
      const localVer = localMetaVersionRef.current[groupId] || 0;
      if (clientVersion && localVer && clientVersion < localVer) {
        // ignore older server update (we have newer local changes)
        return;
      }
      // accept server update
      if (clientVersion) localMetaVersionRef.current[groupId] = clientVersion;
      setStrokes(prev => {
        const filtered = prev.filter(s => s.groupId !== groupId);
        return [...filtered, ...updated].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      });
      // if selected group, update selectedMeta to server canonical
      if (selectedGroupId === groupId) {
        const first = updated[0];
        if (first && first.meta) setSelectedMeta(first.meta);
      }
    });

    socketRef.current.on('removeStrokeGroup', (groupId) => setStrokes(prev => prev.filter(s => s.groupId !== groupId)));

    socketRef.current.on('restoreStrokeGroup', (restored) => {
      setStrokes(prev => {
        const existingIds = new Set(prev.map(s => s.strokeId));
        return [...prev, ...restored.filter(s => !existingIds.has(s.strokeId))];
      });
    });

    socketRef.current.on('eraseGroups', (groupIds) => setStrokes(prev => prev.filter(s => !groupIds.includes(s.groupId))));

    socketRef.current.on('reorderGroup', ({ groupId, timestamp }) => {
      setStrokes(prev => prev.map(s => s.groupId === groupId ? { ...s, timestamp } : s).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    });

    socketRef.current.on('clear', (uname) => {
      setStrokes([]);
      setCurrentPath([]);
      setOverlayMsg(`${uname} cleared the board!`);
      setTimeout(() => setOverlayMsg(null), 2500);
    });

    socketRef.current.on('userList', (list) => {
      setOnlineUsers(list);
      // Update canDraw for current user
      const currentUser = list.find(u => u.userId === data.user.id);
      if (currentUser) {
        setCanUserDraw(currentUser.canDraw);
      }
    });

    socketRef.current.on('chatMessage', (msg) => {
      setChatMessages(prev => [...prev, { ...msg, isMe: false, id: Date.now() + Math.random() }]);
      if (!isSidePanelOpen) {
        setUnreadCount(prev => prev + 1);
      }
    });

    socketRef.current.on('board-settings', (settings) => {
      setBoardSettings(settings);
      setCanUserDraw(settings.canDraw !== false);
    });

    socketRef.current.on('user-permissions', ({ canDraw }) => {
      setCanUserDraw(canDraw);
      setOverlayMsg(canDraw ? 'You can now draw!' : 'Drawing disabled by admin');
      setTimeout(() => setOverlayMsg(null), 2000);
    });

    socketRef.current.on('board-privacy-updated', ({ isPrivate }) => {
      setBoardSettings(prev => ({ ...prev, isPrivate }));
      setOverlayMsg(`Board is now ${isPrivate ? 'private' : 'public'}`);
      setTimeout(() => setOverlayMsg(null), 2000);
    });

    socketRef.current.on('admin-message', ({ message }) => {
      setAdminMessage(message);
      setTimeout(() => setAdminMessage(null), 3000);
    });

    socketRef.current.on('kicked', ({ message }) => {
      setOverlayMsg(message);
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    });

    socketRef.current.on('banned', ({ message }) => {
      setOverlayMsg(message);
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    });

    socketRef.current.on('error', ({ message }) => {
      setOverlayMsg(message);
      setTimeout(() => setOverlayMsg(null), 3000);
    });

    // Fetch existing strokes
    fetch(`${process.env.REACT_APP_APP_URL}/strokes?boardId=${id}`)
      .then(res => res.json())
      .then(s => setStrokes(s))
      .catch(err => console.error('Error fetching strokes:', err));

    connectedRef.current = true;

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      connectedRef.current = false;
    };
  }, [data, id, isSidePanelOpen]);

  useEffect(() => {
    if (activeTab === 'chat' && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTab, isSidePanelOpen]);

  const updateCursor = (x, y, name) => {
    setCursors(prev => ({ ...prev, [name]: { x, y, timestamp: Date.now() } }));
    setTimeout(() => {
      setCursors(prev => {
        const newState = { ...prev };
        if (newState[name] && Date.now() - newState[name].timestamp > 1900) delete newState[name];
        return newState;
      });
    }, 2000);
  };

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0 || !canUserDraw) return;
    const lastAction = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev, lastAction]);

    if (lastAction.type === 'draw') {
      socketRef.current.emit('undo', { action: 'draw', id: lastAction.id });
      setStrokes(prev => prev.filter(s => s.groupId !== lastAction.id));
    }
    else if (lastAction.type === 'erase') {
      socketRef.current.emit('undo', { action: 'erase', ids: lastAction.ids });
      if (lastAction.restoredStrokes) {
        setStrokes(prev => [...prev, ...lastAction.restoredStrokes]);
      }
    }
    else if (lastAction.type === 'update') {
      // revert meta
      const { id, prevMeta } = lastAction;
      const curV = localMetaVersionRef.current[id] || 0;
      const nextV = curV + 1;
      localMetaVersionRef.current[id] = nextV;
      socketRef.current.emit('update-stroke', { groupId: id, meta: prevMeta, userId: data.user.id, clientVersion: nextV });
      setStrokes(prev => prev.map(s => s.groupId === id ? { ...s, meta: prevMeta } : s));
    }
  }, [undoStack, strokes, canUserDraw]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0 || !canUserDraw) return;
    const actionToRedo = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setUndoStack(prev => [...prev, actionToRedo]);

    if (actionToRedo.type === 'draw') {
      socketRef.current.emit('redo', { action: 'draw', id: actionToRedo.id });
    } else if (actionToRedo.type === 'erase') {
      socketRef.current.emit('redo', { action: 'erase', ids: actionToRedo.ids });
      setStrokes(prev => prev.filter(s => !actionToRedo.ids.includes(s.groupId)));
    } else if (actionToRedo.type === 'update') {
      // reapply update: we expect actionToRedo.prevMeta to be previous; store the new meta in undo stack entry we added earlier
      const { id } = actionToRedo;
      // cannot reconstruct newMeta easily here, rely on server broadcast or keep in action
      if (actionToRedo.newMeta) {
        const curV2 = localMetaVersionRef.current[id] || 0;
        const nextV2 = curV2 + 1;
        localMetaVersionRef.current[id] = nextV2;
        socketRef.current.emit('update-stroke', { groupId: id, meta: actionToRedo.newMeta, userId: data.user.id, clientVersion: nextV2 });
        setStrokes(prev => prev.map(s => s.groupId === id ? { ...s, meta: actionToRedo.newMeta } : s));
      }
    }
  }, [redoStack, canUserDraw]);


  const sendChat = () => {
    if (!chatInput.trim()) return;
    socketRef.current.emit('chatMessage', { text: chatInput, uname });
    setChatMessages(prev => [...prev, { sender: 'You', text: chatInput, isMe: true, id: Date.now() }]);
    setChatInput('');
  };

  const getBoardCoords = useCallback((clientX, clientY) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: (clientX - rect.left - view.x) / view.z,
      y: (clientY - rect.top - view.y) / view.z
    };
  }, [view]);

  const handleWheel = useCallback((e) => {
    if (e.ctrlKey) { e.preventDefault(); }
    const zoomIntensity = 0.001;
    const delta = -e.deltaY;
    const scaleMultiplier = 1 + delta * zoomIntensity;
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    setView(prev => {
      let newZ = prev.z * scaleMultiplier;
      newZ = Math.min(Math.max(newZ, 0.1), 5);
      const newX = mouseX - (mouseX - prev.x) * (newZ / prev.z);
      const newY = mouseY - (mouseY - prev.y) * (newZ / prev.z);
      return { x: newX, y: newY, z: newZ };
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (e) => handleWheel(e);
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [handleWheel]);

  const handlePointerDown = (e) => {
    if (e.pointerType === 'touch') {
      e.preventDefault();
    }

    activePointers.current.add(e.pointerId);
    e.target.setPointerCapture(e.pointerId);

    if (activePointers.current.size > 1 || e.button === 1 || isSpacePressed || tool === 'pan') {
      isDrawingRef.current = false;
      setIsPanning(true);
      panStartRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    if (e.button === 0 || e.pointerType === 'touch') {
      const { x, y } = getBoardCoords(e.clientX, e.clientY);
      if (tool === 'select' && selectedGroupId && selectedMeta) {
        const m = selectedMeta;
        const x0 = m.x0 ?? 0, y0 = m.y0 ?? 0, x1 = m.x1 ?? 0, y1 = m.y1 ?? 0;
        const left = Math.min(x0, x1), top = Math.min(y0, y1);
        const right = Math.max(x0, x1), bottom = Math.max(y0, y1);
        const padding = 12 / view.z; // Clickable area for handles

        const corners = {
          tl: { x: left, y: top },
          tr: { x: right, y: top },
          bl: { x: left, y: bottom },
          br: { x: right, y: bottom }
        };

        for (const [corner, pos] of Object.entries(corners)) {
          if (Math.hypot(x - pos.x, y - pos.y) < padding) {
            resizingRef.current = { corner, start: { x, y }, prevMeta: { ...m } };
            resizeStartMetaRef.current = { ...m };
            return; // Stop here, we are resizing
          }
        }
      }

      // Hit-test existing shapes first (select/move) - only when select tool is active
      if (tool === 'select') {
        for (let i = strokes.length - 1; i >= 0; i--) {
          const s = strokes[i];
          if (s.type === 'shape' || s.type === 'text' || !s.type) {
            const m = s.meta || {};
            const hit = (function hitTest(px, py) {
              if (s.type === 'text') {
                const fontSize = m.fontSize || 28;
                const w = (m.text || '').length * (fontSize * 0.6);
                const h = fontSize;
                const left = m.x - 2;
                const top = m.y - h;
                return px >= left && px <= left + w && py >= top && py <= top + h;
              }
              const x0 = m.x0 ?? s.x0;
              const y0 = m.y0 ?? s.y0;
              const x1 = m.x1 ?? s.x1;
              const y1 = m.y1 ?? s.y1;
              const left = Math.min(x0, x1);
              const top = Math.min(y0, y1);
              const w = Math.abs(x1 - x0);
              const h = Math.abs(y1 - y0);
              if (m.shapeType === 'rect' || (!s.type && typeof s.x0 === 'number')) {
                return px >= left && px <= left + w && py >= top && py <= top + h;
              }
              if (m.shapeType === 'ellipse') {
                const cx = left + w / 2;
                const cy = top + h / 2;
                const rx = Math.max(1, w / 2);
                const ry = Math.max(1, h / 2);
                return ((px - cx) ** 2) / (rx * rx) + ((py - cy) ** 2) / (ry * ry) <= 1;
              }
              if (m.shapeType === 'line' || m.shapeType === 'arrow') {
                return distanceToSegment({ x: px, y: py }, { x: x0, y: y0 }, { x: x1, y: y1 }) < (s.width || 4) + 6;
              }
              if (m.shapeType === 'pentagon') {
                const cx = left + w / 2;
                const cy = top + h / 2;
                const rx = Math.max(4, w / 2);
                const ry = Math.max(4, h / 2);
                const pts = [];
                for (let k = 0; k < 5; k++) {
                  const ang = -Math.PI / 2 + (k * 2 * Math.PI) / 5;
                  pts.push({ x: cx + rx * Math.cos(ang), y: cy + ry * Math.sin(ang) });
                }
                let inside = false;
                for (let a = 0, b = pts.length - 1; a < pts.length; b = a++) {
                  const xi = pts[a].x, yi = pts[a].y;
                  const xj = pts[b].x, yj = pts[b].y;
                  const intersect = ((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
                  if (intersect) inside = !inside;
                }
                return inside;
              }
              return false;
            })(x, y);

            if (hit) {
              // select and prepare move
              setSelectedGroupId(s.groupId);
              setSelectedMeta(s.meta ? JSON.parse(JSON.stringify(s.meta)) : {});
              movingRef.current = true;
              moveStartRef.current = { x, y };
              // don't start drawing when selecting
              return;
            }
            if (!hit) {
              // Deselect first
              if (selectedGroupId) {
                setSelectedGroupId(null);
                setSelectedMeta(null);
              }
              // Trigger Pan
              setIsPanning(true);
              panStartRef.current = { x: e.clientX, y: e.clientY };
              return;
            }
          }
        }
      }
    }

    // If user clicked outside any shape, deselect
    if (selectedGroupId) {
      setSelectedGroupId(null);
      setSelectedMeta(null);
    }

    // If no hit, proceed with drawing
    // Allow pen, eraser, shape, and text tools
    if (!['pen', 'eraser', 'shape', 'text'].includes(tool) || !canUserDraw) return;

    const { x: bx, y: by } = getBoardCoords(e.clientX, e.clientY);
    currentGroupId.current = generateId();

    if (tool === 'text') {
      setTextPosition({ x: bx, y: by });
      setTextValue('');
      setTextInputVisible(true);
      return;
    }

    isDrawingRef.current = true;
    lastPosRef.current = { x: bx, y: by };
    currentPathRef.current = [{ x: bx, y: by }];

    if (tool === 'shape') {
      setShapePreview({ x0: bx, y0: by, x1: bx, y1: by });
    }

    setIsDrawing(true);
    setCurrentPath([{ x: bx, y: by }]);

  };
  const handlePointerMove = (e) => {

    if (activePointers.current.size > 1) return;

    if (isPanning) {
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      setView(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
      panStartRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    const { x, y } = getBoardCoords(e.clientX, e.clientY);
    if (e.pointerType === 'mouse') setCursorPos({ x, y });

    // If currently resizing selected shape
    if (resizingRef.current && selectedGroupId) {
      const { corner, prevMeta } = resizingRef.current;
      const newMeta = { ...prevMeta };

      if (corner.includes('r')) newMeta.x1 = x;
      if (corner.includes('l')) newMeta.x0 = x;
      if (corner.includes('b')) newMeta.y1 = y;
      if (corner.includes('t')) newMeta.y0 = y;

      setSelectedMeta(newMeta);
      // Real-time update for other users
      setStrokes(prev => prev.map(s => s.groupId === selectedGroupId ? { ...s, meta: newMeta } : s));
      return;
    }

    // If currently moving a selected shape, update its position
    if (movingRef.current && selectedGroupId) {
      const prev = moveStartRef.current;
      const dx = x - prev.x;
      const dy = y - prev.y;
      if (dx === 0 && dy === 0) return;
      moveStartRef.current = { x, y };
      setStrokes(sts => sts.map(s => {
        if (s.groupId !== selectedGroupId) return s;
        const m = s.meta ? { ...s.meta } : {};
        if (m.x != null && m.y != null && m.text) {
          m.x += dx; m.y += dy;
        } else if (m.x0 != null && m.y0 != null) {
          m.x0 += dx; m.y0 += dy; m.x1 += dx; m.y1 += dy;
        } else if (s.x0 != null) {
          // legacy line
          return { ...s, x0: s.x0 + dx, y0: s.y0 + dy, x1: s.x1 + dx, y1: s.y1 + dy };
        }
        setSelectedMeta(m);
        return { ...s, meta: m };
      }));
      return;
    }

    if (isDrawingRef.current && canUserDraw) {
      if (tool === 'pen') {
        const newStroke = {
          strokeId: generateId(),
          groupId: currentGroupId.current,
          x0: lastPosRef.current.x,
          y0: lastPosRef.current.y,
          x1: x,
          y1: y,
          color: strokeColor,
          width: strokeWidth,
          userId: data.user.id,
        };

        socketRef.current.emit('stroke', { stroke: newStroke, uname, userId: data.user.id });
        setStrokes(prev => [...prev, newStroke]);

        currentPathRef.current.push({ x, y });
        lastPosRef.current = { x, y };
        setCurrentPath([...currentPathRef.current]);

      } else if (tool === 'eraser') {
        let batchHasChanges = false;
        const now = Date.now();
        const eraserSlack = ERASER_RADIUS;

        // 1. Calculate Eraser Bounds
        const eraserMinX = x - eraserSlack;
        const eraserMaxX = x + eraserSlack;
        const eraserMinY = y - eraserSlack;
        const eraserMaxY = y + eraserSlack;

        // 2. Get Candidate Strokes from Grid (Spatial Lookup O(1))
        const gridKeys = getGridKeys(eraserMinX, eraserMinY, eraserMaxX, eraserMaxY);
        const candidates = new Set();

        gridKeys.forEach(key => {
          const items = spatialGridRef.current.get(key);
          if (items) {
            for (let i = 0; i < items.length; i++) {
              candidates.add(items[i]);
            }
          }
        });

        // 3. Iterate ONLY candidates
        candidates.forEach(({ stroke: s, bounds }) => {
          // Skip if already erased
          if (erasedIdsRef.current.has(s.groupId)) return;

          // Permission Check
          if (boardSettings.eraseOwnOnly && !isAdmin && s.userId !== data.user.id) return;

          // Fast Bound Check (using pre-calculated bounds)
          if (x < bounds.minX - eraserSlack || x > bounds.maxX + eraserSlack ||
            y < bounds.minY - eraserSlack || y > bounds.maxY + eraserSlack) {
            return;
          }

          // Detailed Hit Test
          const m = s.meta || {};
          const shapeType = m.shapeType || s.type;
          let hitErase = false;
          const x0 = m.x0 ?? s.x0 ?? m.x;
          const y0 = m.y0 ?? s.y0 ?? m.y;
          const x1 = m.x1 ?? s.x1 ?? (m.x ? m.x + (m.width || 100) : x0);
          const y1 = m.y1 ?? s.y1 ?? (m.y ? m.y + (m.fontSize || 30) : y0);

          if (shapeType === 'line' || shapeType === 'arrow') {
            const dist = distanceToSegment({ x, y }, { x: x0, y: y0 }, { x: x1, y: y1 });
            hitErase = dist < ERASER_RADIUS + (s.width || 4);
          } else if (shapeType === 'text') {
            // Simple Box for text
            hitErase = (x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY);
          } else {
            // Shapes (Rect, Ellipse, etc) - keeping your existing math
            if (shapeType === 'ellipse') {
              const w = Math.abs(x1 - x0);
              const h = Math.abs(y1 - y0);
              const left = Math.min(x0, x1);
              const top = Math.min(y0, y1);
              const cx = left + w / 2;
              const cy = top + h / 2;
              const rx = Math.max(1, w / 2);
              const ry = Math.max(1, h / 2);
              hitErase = ((x - cx) ** 2) / (rx * rx) + ((y - cy) ** 2) / (ry * ry) <= 1;
            } else {
              // Rect / Pentagon (simplified box for speed, or keep your Poly check if precise)
              hitErase = (x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY);
            }
          }

          if (hitErase) {
            erasedIdsRef.current.add(s.groupId);
            batchHasChanges = true;
          }
        });

        // 4. Throttle React Updates
        if (batchHasChanges) {
          if (now - lastEraserUpdateRef.current > 16) { // 16ms = ~60fps
            setErasedIds(new Set(erasedIdsRef.current));
            lastEraserUpdateRef.current = now;
          }
        }
      } else if (tool === 'shape') {
        setShapePreview(prev => prev ? ({ ...prev, x1: x, y1: y }) : { x0: x, y0: y, x1: x, y1: y });
      }
    }
  };

  const handlePointerUp = (e) => {
    activePointers.current.delete(e.pointerId);

    // finalize resizing
    if (resizingRef.current && selectedGroupId) {
      const prevMeta = resizeStartMetaRef.current || resizingRef.current.prevMeta || {};
      const newMeta = selectedMeta || {};
      // push undo entry for update
      setUndoStack(prev => [...prev, { type: 'update', id: selectedGroupId, prevMeta, newMeta }]);
      setRedoStack([]);
      const cur = localMetaVersionRef.current[selectedGroupId] || 0;
      const next = cur + 1;
      localMetaVersionRef.current[selectedGroupId] = next;
      socketRef.current.emit('update-stroke', { groupId: selectedGroupId, meta: newMeta, userId: data.user.id, clientVersion: next });
      resizingRef.current = null;
      resizeStartMetaRef.current = null;
    }

    // finalize moving
    if (movingRef.current && selectedGroupId) {
      movingRef.current = false;
      const metaToSend = selectedMeta || {};
      // store previous meta for undo
      const prevMeta = resizeStartMetaRef.current || (strokes.find(s => s.groupId === selectedGroupId)?.meta) || {};
      const newMeta = metaToSend;
      setUndoStack(prev => [...prev, { type: 'update', id: selectedGroupId, prevMeta, newMeta }]);
      setRedoStack([]);
      const cur = localMetaVersionRef.current[selectedGroupId] || 0;
      const next = cur + 1;
      localMetaVersionRef.current[selectedGroupId] = next;
      socketRef.current.emit('update-stroke', { groupId: selectedGroupId, meta: metaToSend, userId: data.user.id, clientVersion: next });
      setStrokes(prev => prev.map(s => s.groupId === selectedGroupId ? { ...s, meta: metaToSend } : s));
      // keep selection active after move
    }

    if (isDrawingRef.current && canUserDraw) {
      if (tool === 'pen' && currentPathRef.current.length > 1) {
        setUndoStack(prev => [...prev, { type: 'draw', id: currentGroupId.current }]);
        setRedoStack([]);
      } else if (tool === 'shape' && shapePreview) {
        // finalize shape
        const s = shapePreview;
        const newStroke = {
          strokeId: generateId(),
          groupId: currentGroupId.current,
          type: 'shape',
          meta: {
            shapeType: shapeKind,
            x0: s.x0, y0: s.y0, x1: s.x1, y1: s.y1,
            fill: fillColor,
            strokeColor: strokeColor,
            strokeWidth: strokeWidth
          },
          color: strokeColor,
          width: strokeWidth,
          userId: data.user.id,
        };
        socketRef.current.emit('stroke', { stroke: newStroke, uname, userId: data.user.id });
        setStrokes(prev => [...prev, newStroke]);
        setUndoStack(prev => [...prev, { type: 'draw', id: currentGroupId.current }]);
        setRedoStack([]);
        setShapePreview(null);
      }
      else if (tool === 'eraser' && erasedIdsRef.current.size > 0) {
        setErasedIds(new Set());
        const idsArray = Array.from(erasedIdsRef.current);
        const strokesToRestore = strokes.filter(s => erasedIdsRef.current.has(s.groupId));
        socketRef.current.emit('erase', { groupIds: idsArray, userId: data.user.id });
        setStrokes(prev => prev.filter(s => !erasedIdsRef.current.has(s.groupId)));
        setUndoStack(prev => [...prev, { type: 'erase', ids: idsArray, restoredStrokes: strokesToRestore }]);
        setRedoStack([]);
      }
    }
    isDrawingRef.current = false;
    currentPathRef.current = [];
    erasedIdsRef.current = new Set();
    setIsDrawing(false);
    setIsPanning(false);
    setCurrentPath([]);
    if (e.target.hasPointerCapture(e.pointerId)) {
      e.target.releasePointerCapture(e.pointerId);
    }
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault(); // Stop browser zoom
      const t1 = e.touches[0];
      const t2 = e.touches[1];

      // 1. Calculate and store initial distance (for Zooming)
      touchDistRef.current = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

      // 2. Calculate and store initial center point (for Panning)
      touchCenterRef.current = {
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2,
      };
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const t1 = e.touches[0];
      const t2 = e.touches[1];

      // A. Calculate new Zoom (Distance between fingers)
      const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

      // B. Calculate new Center (Average position of fingers)
      const cx = (t1.clientX + t2.clientX) / 2;
      const cy = (t1.clientY + t2.clientY) / 2;

      if (touchDistRef.current && touchCenterRef.current) {
        const scaleFactor = dist / touchDistRef.current;

        // Calculate how much the fingers moved (Pan amount)
        const panDx = cx - touchCenterRef.current.x;
        const panDy = cy - touchCenterRef.current.y;

        setView(prev => {
          // 1. Apply Zoom
          const newZ = Math.min(Math.max(prev.z * scaleFactor, 0.1), 5);

          // Calculate zoom pivot based on screen coordinates
          const rect = containerRef.current.getBoundingClientRect();
          const relCx = cx - rect.left;
          const relCy = cy - rect.top;

          // Standard zoom math (zooms towards the center of your fingers)
          let newX = relCx - (relCx - prev.x) * (newZ / prev.z);
          let newY = relCy - (relCy - prev.y) * (newZ / prev.z);

          // 2. Apply Pan (Add the finger movement)
          newX += panDx;
          newY += panDy;

          return { x: newX, y: newY, z: newZ };
        });

        // Update refs for the next frame
        touchDistRef.current = dist;
        touchCenterRef.current = { x: cx, y: cy };
      }
    }
  };
  const handleManualZoom = (direction) => {
    const centerFactor = 1.2;
    setView(prev => {
      const newZ = direction > 0 ? prev.z * centerFactor : prev.z / centerFactor;
      const rect = containerRef.current.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const newX = cx - (cx - prev.x) * (newZ / prev.z);
      const newY = cy - (cy - prev.y) * (newZ / prev.z);
      return { x: newX, y: newY, z: Math.min(Math.max(newZ, 0.1), 5) };
    });
  };

  const finalizeText = (confirmedText) => {
    const text = (confirmedText || textValue || '').trim();
    if (!text) {
      setTextInputVisible(false);
      return;
    }

    const newStroke = {
      strokeId: generateId(),
      groupId: currentGroupId.current || generateId(),
      type: 'text',
      meta: {
        text,
        x: textPosition.x,
        y: textPosition.y,
        fontSize: textFontSize,
        color: strokeColor,
        fontFamily: 'Permanent Marker, cursive'
      },
      color: strokeColor,
      width: 0,
      userId: data.user.id
    };

    socketRef.current.emit('stroke', { stroke: newStroke, uname, userId: data.user.id });
    setStrokes(prev => [...prev, newStroke]);
    setUndoStack(prev => [...prev, { type: 'draw', id: newStroke.groupId }]);
    setRedoStack([]);
    setTextInputVisible(false);
    setTextValue('');
  };

  const updateSelectedMeta = (newMeta, emit = true) => {
    // Determine if this group contains shape/meta strokes or legacy line strokes
    const groupStrokes = strokes.filter(s => s.groupId === selectedGroupId);
    const hasShape = groupStrokes.some(s => s.type === 'shape' || (s.meta && Object.keys(s.meta).length > 0));
    const prevMeta = groupStrokes[0]?.meta || null;

    if (hasShape) {
      // update meta on shapes
      setSelectedMeta(newMeta);
      setStrokes(prevSt => prevSt.map(s => s.groupId === selectedGroupId ? { ...s, meta: newMeta } : s));
      if (emit && selectedGroupId) {
        setUndoStack(u => [...u, { type: 'update', id: selectedGroupId, prevMeta, newMeta }]);
        setRedoStack([]);
        const cur = localMetaVersionRef.current[selectedGroupId] || 0;
        const next = cur + 1;
        localMetaVersionRef.current[selectedGroupId] = next;
        socketRef.current.emit('update-stroke', { groupId: selectedGroupId, meta: newMeta, userId: data.user.id, clientVersion: next });
      }
    } else {
      // legacy strokes: update top-level color
      const prevColor = groupStrokes[0]?.color || strokeColor;
      setStrokes(prevSt => prevSt.map(s => s.groupId === selectedGroupId ? { ...s, color: newMeta.strokeColor || newMeta.color } : s));
      if (emit && selectedGroupId) {
        const newColor = newMeta.strokeColor || newMeta.color;
        setUndoStack(u => [...u, { type: 'update', id: selectedGroupId, prevMeta: { color: prevColor }, newMeta: { color: newColor } }]);
        setRedoStack([]);
        // send color change to server using same event; server will update color if provided
        const cur = localMetaVersionRef.current[selectedGroupId] || 0;
        const next = cur + 1;
        localMetaVersionRef.current[selectedGroupId] = next;
        socketRef.current.emit('update-stroke', { groupId: selectedGroupId, color: newColor, userId: data.user.id, clientVersion: next });
      }
    }
  };

  const RenderedStrokes = useMemo(() => {
    // sort by timestamp so reorder works consistently
    const sorted = [...strokes].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return sorted.map((s, i) => {
      const isErased = erasedIdsRef.current.has(s.groupId);
      const opacity = isErased ? 0.2 : 1;

      // default to legacy line
      if (!s.type || s.type === 'line') {
        return (
          <line key={s.strokeId || i} x1={s.x0} y1={s.y0} x2={s.x1} y2={s.y1}
            stroke={s.color} strokeWidth={s.width} strokeLinecap="round"
            opacity={opacity}
          />
        );
      }

      if (s.type === 'shape') {
        const m = s.meta || {};
        const x0 = m.x0 ?? s.x0;
        const y0 = m.y0 ?? s.y0;
        const x1 = m.x1 ?? s.x1;
        const y1 = m.y1 ?? s.y1;
        const left = Math.min(x0, x1);
        const top = Math.min(y0, y1);
        const w = Math.abs(x1 - x0);
        const h = Math.abs(y1 - y0);
        const strokeCol = m.strokeColor || s.color;
        const strokeW = m.strokeWidth ?? s.width;
        const fillCol = m.fill || 'none';
        if (m.shapeType === 'rect') {
          return <rect key={s.strokeId || i} x={left} y={top} width={w} height={h} fill={fillCol} stroke={strokeCol} strokeWidth={strokeW} opacity={opacity} />;
        }
        if (m.shapeType === 'ellipse') {
          return <ellipse key={s.strokeId || i} cx={left + w / 2} cy={top + h / 2} rx={Math.max(1, w / 2)} ry={Math.max(1, h / 2)} fill={fillCol} stroke={strokeCol} strokeWidth={strokeW} opacity={opacity} />;
        }
        if (m.shapeType === 'arrow') {
          const xA = x0, yA = y0, xB = x1, yB = y1;
          const angle = Math.atan2(yB - yA, xB - xA);
          const headLen = Math.max(8, strokeW * 4);
          const hx1 = xB - headLen * Math.cos(angle - Math.PI / 6);
          const hy1 = yB - headLen * Math.sin(angle - Math.PI / 6);
          const hx2 = xB - headLen * Math.cos(angle + Math.PI / 6);
          const hy2 = yB - headLen * Math.sin(angle + Math.PI / 6);
          return (
            <g key={s.strokeId || i} opacity={opacity}>
              <line x1={xA} y1={yA} x2={xB} y2={yB} stroke={strokeCol} strokeWidth={strokeW} strokeLinecap="round" />
              <polygon points={`${xB},${yB} ${hx1},${hy1} ${hx2},${hy2}`} fill={strokeCol} />
            </g>
          );
        }
        if (m.shapeType === 'pentagon') {
          const cx = left + w / 2;
          const cy = top + h / 2;
          const rx = Math.max(4, w / 2);
          const ry = Math.max(4, h / 2);
          const pts = [];
          for (let k = 0; k < 5; k++) {
            const ang = -Math.PI / 2 + (k * 2 * Math.PI) / 5;
            pts.push(`${cx + rx * Math.cos(ang)},${cy + ry * Math.sin(ang)}`);
          }
          return <polygon key={s.strokeId || i} points={pts.join(' ')} fill={fillCol} stroke={strokeCol} strokeWidth={strokeW} opacity={opacity} />;
        }
        // line fallback for shape
        return <line key={s.strokeId || i} x1={x0} y1={y0} x2={x1} y2={y1} stroke={strokeCol} strokeWidth={strokeW} strokeLinecap="round" opacity={opacity} />;
      }

      if (s.type === 'text') {
        const m = s.meta || {};
        const fontSize = m.fontSize || 28;
        const fontFamily = m.fontFamily || 'Permanent Marker, cursive';
        return (
          <text key={s.strokeId || i} x={m.x} y={m.y} fill={m.color || s.color} fontSize={fontSize} fontFamily={`"${fontFamily}"`} opacity={opacity}>
            {m.text}
          </text>
        );
      }

      // unknown type -> ignore
      return null;
    });
  }, [strokes, erasedIds]);

  const currentPathString = useMemo(() => {
    if (currentPath.length < 2) return "";
    return currentPath.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(" ");
  }, [currentPath]);

  const handleDownload = () => {
    const svgElement = containerRef.current.querySelector('svg');
    if (!svgElement) return;

    const svgClone = svgElement.cloneNode(true);
    const svgData = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    if (downloadSettings.format === 'svg') {
      const link = document.createElement("a");
      link.href = url;
      link.download = `doodle-${id}.svg`;
      link.click();
      setShowDownloadModal(false);
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = BOARD_WIDTH;
    canvas.height = BOARD_HEIGHT;

    const img = new Image();
    img.onload = () => {
      if (downloadSettings.background === 'patterned') {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#cbd5e1";
        for (let x = 0; x < BOARD_WIDTH; x += 24) {
          for (let y = 0; y < BOARD_HEIGHT; y += 24) {
            ctx.beginPath();
            ctx.arc(x, y, 1.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      } else {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(img, 0, 0);

      const mimeType = downloadSettings.format === 'jpg' ? 'image/jpeg' : 'image/png';
      const finalUrl = canvas.toDataURL(mimeType, downloadSettings.quality);

      const downloadLink = document.createElement("a");
      downloadLink.href = finalUrl;
      downloadLink.download = `doodle-${id}.${downloadSettings.format}`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      setShowDownloadModal(false);
    };
    img.src = url;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleSystemShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'DoodleUp Collaborative Board',
          text: 'Join my drawing board on DoodleUp!',
          url: window.location.href,
        });
      } catch (err) { console.log('Share failed', err); }
    } else {
      handleCopyLink();
    }
  };

  const handleTogglePrivacy = async () => {
    const newPrivacy = !boardSettings.isPrivate;
    socketRef.current.emit('admin-toggle-privacy', {
      isPrivate: newPrivacy,
      allowedUsers: newPrivacy ? allowedUsers : [],
      adminUserId: data.user.id
    });
  };

  const handleKickUser = (userId, username) => {
    if (window.confirm(`Kick ${username} from the board?`)) {
      socketRef.current.emit('admin-kick-user', {
        targetUserId: userId,
        adminUserId: data.user.id
      });
    }
  };

  const handleBanUser = (userId, username) => {
    if (window.confirm(`Ban ${username} from this board? They won't be able to rejoin.`)) {
      socketRef.current.emit('admin-ban-user', {
        targetUserId: userId,
        adminUserId: data.user.id
      });
    }
  };

  const handleToggleUserDraw = (userId, username, canDraw) => {
    socketRef.current.emit('admin-toggle-draw', {
      targetUserId: userId,
      canDraw: !canDraw,
      adminUserId: data.user.id
    });
  };

  const handleUpdateSettings = () => {
    socketRef.current.emit('admin-update-settings', {
      settings: boardSettings,
      adminUserId: data.user.id
    });
  };

  const handleClearBoard = () => {
    if (window.confirm('Clear the entire board? This cannot be undone.')) {
      socketRef.current.emit('clear', { userId: data.user.id, uname });
    }
  };
  const handleAddAllowedUser = async () => {
    if (!userSearch.trim()) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_APP_URL}/board/${id}/manage-user`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userSearch,
          action: 'allow-access'
        })
      });

      const result = await res.json();
      if (result.req) {
        setAllowedUsers(prev => [...prev, userSearch]);
        setUserSearch('');
        setOverlayMsg('User added to allowed list');
        setTimeout(() => setOverlayMsg(null), 2000);
      }
    } catch (err) {
      console.error('Add allowed user error:', err);
    }
  };

  const handleRemoveAllowedUser = (userId) => {
    setAllowedUsers(prev => prev.filter(id => id !== userId));
  };
  //   useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     const isCtrlOrCmd = e.ctrlKey || e.metaKey;
  //     if (isCtrlOrCmd) {
  //     // ZOOM IN: Checks for '+' (numpad), '=' (standard key for +), or 'Equal' code
  //     if (e.key === '=' || e.key === '+' || e.code === 'Equal' || e.code === 'NumpadAdd') {
  //       e.preventDefault();
  //       // Use the REF to call the latest function
  //       handleManualZoom(1);
  //     }

  //     // ZOOM OUT: Checks for '-' or 'Minus' code
  //     if (e.key === '-' || e.code === 'Minus' || e.code === 'NumpadSubtract') {
  //       e.preventDefault();
  //       handleManualZoom(-1);
  //     }
  //     if (e.key === 'z' || e.code === 'KeyZ') {
  //       // Fix: Check the REF length, not the stale state variable
  //       if (undoStack.length > 0) {
  //         e.preventDefault();
  //         handleUndo(); // Ensure handleUndo doesn't take arguments if not needed
  //       }
  //     }
      
  //      // REDO: usually Cmd+Shift+Z or Ctrl+Y
  //      if ((e.shiftKey && e.key === 'z') || e.key === 'y') {
  //        // Logic for redo if you have it
  //      }
  //     window.addEventListener('keydown', handleKeyDown, { passive: false });
  
  // return () => window.removeEventListener('keydown', handleKeyDown);
  //   }
  //   };
  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => window.removeEventListener('keydown', handleKeyDown);
  // }, []);

  const login = async (username) => {
    const res = await fetch(process.env.REACT_APP_APP_URL + '/login', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username })
    });
    const d = await res.json();
    setData(d);
  };

  if (loadingBoard || !data) return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-gray-900 flex items-center justify-center font-marker text-2xl text-slate-400 dark:text-slate-600 animate-pulse p-4">
      <div className="text-center">
        Unpacking Supplies...
      </div>
    </div>
  );

  if (!data.req) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa] dark:bg-gray-900 p-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 dark:opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
        <div className="bg-white dark:bg-gray-800 p-6 sm:p-8 md:p-10 rounded-2xl shadow-[8px_8px_0_0_#1e293b] dark:shadow-[8px_8px_0_0_#0f172a] max-w-md w-full border-[3px] border-slate-800 dark:border-slate-700 text-center relative z-10 mx-4">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-300 dark:bg-yellow-500 border-2 border-slate-800 dark:border-slate-700 px-3 py-1 text-xs font-bold -rotate-2 shadow-sm whitespace-nowrap">
            GUEST ACCESS
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-marker text-slate-800 dark:text-white mb-6">DoodleUp</h1>
          <input
            className="w-full p-3 sm:p-4 border-[3px] border-slate-200 dark:border-slate-600 rounded-xl mb-6 font-bold text-lg sm:text-xl outline-none focus:border-indigo-500 dark:focus:border-indigo-400 focus:shadow-[4px_4px_0_0_#6366f1] dark:focus:shadow-[4px_4px_0_0_#818cf8] transition-all bg-slate-50 dark:bg-gray-700 dark:text-white border-dashed"
            placeholder="Nickname..."
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <button
            onClick={(e) => { e.preventDefault(); login(name); }}
            className="w-full bg-slate-800 dark:bg-slate-700 text-white font-marker text-lg sm:text-xl py-3 sm:py-4 rounded-xl shadow-[4px_4px_0_0_#94a3b8] dark:shadow-[4px_4px_0_0_#475569] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_0_#94a3b8] dark:hover:shadow-[2px_2px_0_0_#475569] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-all border-2 border-slate-800 dark:border-slate-600"
          >
            Start Scribbling
          </button>
        </div>
      </div>
    );
  }

  let cursorClass = 'cursor-default';
  if (tool === 'eraser') cursorClass = 'cursor-none';
  else if (isPanning || tool === 'pan' || isSpacePressed) cursorClass = isPanning ? 'cursor-grabbing' : 'cursor-grab';
  else if (tool === 'select') cursorClass = 'cursor-default';
  else cursorClass = 'cursor-crosshair';

  return (
    <div className='h-screen w-full flex flex-col bg-[#FCF8F8] dark:bg-[#1a1111] overflow-hidden font-sans select-none relative text-[#452829] dark:text-[#F9DFDF]'>

      {/* --- Global Styles & Fonts --- */}
      <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;700;800&family=Permanent+Marker&display=swap');
      .font-marker { font-family: 'Permanent Marker', cursive; }
      .font-body { font-family: 'Outfit', sans-serif; }
      
      /* Professional Dot Pattern */

      .bg-graph-pattern {
            background-color: #ffffff;
            background-image: radial-gradient(#cbd5e1 1.5px, transparent 1.5px);
            background-size: 24px 24px;
        }
        
        .dark .bg-graph-pattern {
            background-color: #ffffff;
            background-image: radial-gradient(#cbd5e1 1.5px, transparent 1.5px);
            background-size: 24px 24px;
        }

      /* Scrollbar hiding for clean UI */
      .no-scrollbar::-webkit-scrollbar { display: none; }
      .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        .cursor-none { cursor: none; } 
        .touch-action-none { touch-action: none; }
    `}</style>

      {/* =========================================================================
        TOP NAVIGATION BAR (Glassmorphic Header)
       ========================================================================= */}
      <div className="absolute top-0 left-0 right-0 z-50 px-3 py-3 sm:px-4 pointer-events-none">
        <div className="max-w-[1920px] mx-auto grid grid-cols-[auto_1fr_auto] items-center gap-2">
          <div className="pointer-events-auto flex justify-start">
            <Link to="/" className="flex items-center gap-2 group bg-[#FBEFEF]/90 dark:bg-[#2d1f1f]/90 backdrop-blur-md sm:px-5 sm:py-2 p-1 sm:rounded-full rounded-lg border border-[#452829]/10 dark:border-[#F5AFAF]/20 shadow-sm transition-transform hover:scale-105">
              <span className='bg-[#452829] dark:bg-[#F5AFAF] text-[#FCF8F8] dark:text-[#452829] p-1.5 rounded-lg group-hover:rotate-12 transition-transform duration-300'>
                {Icons.Doodle}
              </span>
              <span className="font-body font-bold text-lg tracking-tight hidden sm:block text-[#452829] dark:text-[#FBEFEF]">
                doodle<span className='text-[#F5AFAF]'>up</span>
              </span>
            </Link>
          </div>

          {/* --- CENTER: ZOOM & UNDO (Always Visible) --- */}
          <div className="pointer-events-auto flex justify-center">
            <div className="flex items-center gap-1 sm:gap-2 bg-[#FBEFEF]/90 dark:bg-[#2d1f1f]/90 backdrop-blur-md px-2 py-1.5 sm:px-3 sm:py-1.5 rounded-full border border-[#452829]/10 dark:border-[#F5AFAF]/20 shadow-sm">

              {/* Zoom Controls */}
              <div className="flex items-center">
                <button onClick={() => handleManualZoom(-1)} className="p-1.5 sm:p-2 hover:bg-[#452829]/5 rounded-full text-[#452829] dark:text-[#F9DFDF] transition-colors">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </button>
                <span className="w-9 sm:w-12 text-center font-bold text-xs sm:text-sm tabular-nums text-[#452829] dark:text-[#F9DFDF]">
                  {Math.round(view.z * 100)}%
                </span>
                <button onClick={() => handleManualZoom(1)} className="p-1.5 sm:p-2 hover:bg-[#452829]/5 rounded-full text-[#452829] dark:text-[#F9DFDF] transition-colors">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </button>
              </div>

              {/* Divider */}
              <div className="h-4 sm:h-5 w-px bg-[#452829]/10 dark:bg-[#FBEFEF]/10 mx-0.5"></div>

              {/* Undo/Redo */}
              <div className="flex items-center gap-0.5">
                <button
                  onClick={handleUndo}
                  disabled={undoStack.length === 0}
                  className="p-1.5 sm:p-2 hover:bg-[#452829]/5 rounded-full text-[#452829] dark:text-[#F9DFDF] disabled:opacity-30 transition-colors"
                >
                  {Icons.Undo}
                </button>
                <button
                  onClick={handleRedo}
                  disabled={redoStack.length === 0}
                  className="p-1.5 sm:p-2 hover:bg-[#452829]/5 rounded-full text-[#452829] dark:text-[#F9DFDF] disabled:opacity-30 transition-colors"
                >
                  {Icons.Redo}
                </button>
              </div>

            </div>
          </div>

          {/* --- RIGHT: ACTIONS & PROFILE --- */}
          <div className="pointer-events-auto flex items-center justify-end gap-3" ref={profileRef}>

            {/* 1. DESKTOP ACTIONS (Hidden on Mobile) */}
            <div className="hidden md:flex items-center gap-2 bg-[#FBEFEF]/90 dark:bg-[#2d1f1f]/90 backdrop-blur-md p-1.5 rounded-full border border-[#452829]/10 dark:border-[#F5AFAF]/20 shadow-sm">
              {isAdmin && (
                <button
                  onClick={() => setShowAdminModal(true)}
                  className="w-10 h-10 flex items-center justify-center bg-[#452829] text-white rounded-full hover:bg-[#2a1818] transition-all"
                  title="Admin"
                >
                  {Icons.Crown}
                </button>
              )}
              <button
                onClick={() => setShowShareModal(true)}
                className="w-10 h-10 flex items-center justify-center bg-[#F5AFAF] text-[#452829] rounded-full hover:brightness-110 transition-all font-bold"
                title="Share"
              >
                {Icons.Share}
              </button>
              <button
                onClick={() => setShowDownloadModal(true)}
                className="flex items-center gap-2 px-4 h-10 bg-[#FCF8F8] dark:bg-[#3d2b2b] text-[#452829] dark:text-[#FBEFEF] rounded-full hover:bg-[#F9DFDF] dark:hover:bg-[#3d2b2b]/10 transition-all font-bold text-sm"
              >
                {Icons.Download} Export
              </button>
            </div>

            {/* 2. PROFILE BUTTON */}
            {!data ? (
              <div className="w-10 h-10 bg-[#FBEFEF] dark:bg-[#2d1f1f] rounded-full animate-pulse"></div>
            ) : !data?.req ? (
              <a
                href={`${process.env.REACT_APP_ACCOUNT_URL}/a/login?re=${process.env.REACT_APP_URL}`}
                className="flex items-center gap-2 px-4 py-2 bg-[#452829] text-[#FBEFEF] text-sm font-bold rounded-full hover:bg-[#2a1818] transition-all shadow-md"
              >
                Login
              </a>
            ) : (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className={`relative w-10 h-10 sm:w-11 sm:h-11 rounded-full p-0.5 transition-all duration-200 ${isProfileOpen ? 'ring-2 ring-[#F5AFAF] bg-[#F5AFAF]' : 'bg-[#FBEFEF] dark:bg-[#2d1f1f] hover:ring-2 hover:ring-[#452829]/20'}`}
                >
                  <img
                    className="w-full h-full rounded-full object-cover border border-[#FCF8F8] dark:border-[#452829]"
                    src={data.user.profile_p}
                    alt="profile"
                  />
                </button>

                {/* MENU DROPDOWN */}
                <AnimatePresence>
                  {isProfileOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10 }}
                      className="absolute right-0 top-14 w-64 bg-[#FCF8F8] dark:bg-[#1a1111] border border-[#452829]/10 dark:border-[#F5AFAF]/20 rounded-2xl shadow-2xl shadow-[#452829]/20 z-50 overflow-hidden"
                    >

                      {/* User Info */}
                      <div className="px-5 py-4 bg-[#FBEFEF]/50 dark:bg-[#2d1f1f]/50 border-b border-[#452829]/5">
                        <h2 className="text-sm font-bold text-[#452829] dark:text-[#F9DFDF] truncate">
                          {data.auth ? `${data.user.firstName} ${data.user.lastName}` : (data.user.username || 'Guest User')}
                        </h2>
                        <p className="text-xs text-[#452829]/60 dark:text-[#FBEFEF]/60 truncate mt-0.5">
                          {data.auth ? data.user.email : 'Guest Account'}
                        </p>
                      </div>

                      <div className="py-2 flex flex-col">

                        {/* --- MOBILE ONLY ACTIONS (Visible < md) --- */}
                        <div className="md:hidden block">
                          {isAdmin && (
                            <button
                              onClick={() => { setShowAdminModal(true); setIsProfileOpen(false); }}
                              className="flex items-center gap-3 px-4 py-2 text-sm font-bold text-[#452829] dark:text-[#F9DFDF] hover:bg-[#FBEFEF] dark:hover:bg-[#2d1f1f] transition-colors text-left w-full"
                            >
                              <span className="w-8 h-8 flex items-center justify-center bg-[#452829] text-[#FCF8F8] rounded-full text-[10px]">{Icons.Crown}</span>
                              Admin Panel
                            </button>
                          )}

                          <button
                            onClick={() => { setShowShareModal(true); setIsProfileOpen(false); }}
                            className="flex items-center gap-3 px-4 py-2 text-sm font-bold text-[#452829] dark:text-[#F9DFDF] hover:bg-[#FBEFEF] dark:hover:bg-[#2d1f1f] transition-colors text-left w-full"
                          >
                            <span className="w-8 h-8 flex items-center justify-center bg-[#F5AFAF] text-[#452829] rounded-full text-xs">{Icons.Share}</span>
                            Share Board
                          </button>

                          <button
                            onClick={() => { setShowDownloadModal(true); setIsProfileOpen(false); }}
                            className="flex items-center gap-3 px-4 py-2 text-sm font-bold text-[#452829] dark:text-[#F9DFDF] hover:bg-[#FBEFEF] dark:hover:bg-[#2d1f1f] transition-colors text-left w-full"
                          >
                            <span className="w-8 h-8 flex items-center justify-center bg-[#FCF8F8] border border-[#452829]/10 text-[#452829] rounded-full text-xs">{Icons.Download}</span>
                            Export Image
                          </button>

                          <div className="my-1.5 h-px bg-[#452829]/10 dark:bg-[#F5AFAF]/10 w-full"></div>
                        </div>

                        {/* --- ACCOUNT ACTIONS (Always Visible) --- */}
                        {data.auth ? (
                          <>
                            <a
                              href={`${process.env.REACT_APP_ACCOUNT_URL}/u`}
                              target='_blank'
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-[#452829]/80 dark:text-[#FBEFEF]/80 hover:bg-[#FBEFEF] dark:hover:bg-[#2d1f1f] transition-colors"
                            >
                              Account Settings
                            </a>
                            <a
                              href={`${process.env.REACT_APP_ACCOUNT_URL}/a/logout`}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                            >
                              Log Out
                            </a>
                          </>
                        ) : (
                          <div className="px-4 py-2 text-xs text-[#452829]/40 italic text-center">
                            Log in to save your settings
                          </div>
                        )}
                        <ThemeSwitcher />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* =========================================================================
        MAIN TOOLBAR (Adaptive: Bottom on Mobile, Left on Desktop)
       ========================================================================= */}
      {canUserDraw && <div className="fixed z-40 
    /* Mobile: Bottom Dock Wrapper */
    bottom-6 left-4 right-4 flex flex-col items-start
    /* Desktop: Left Sidebar Wrapper */
    sm:top-1/2 sm:-translate-y-1/2 sm:left-4 sm:right-auto sm:bottom-auto sm:flex-row sm:items-start"
      >

        {/* 1. SETTINGS POPUP MENU 
      Placed BEFORE the scrollable tools so it renders "above" the dock visually in DOM order 
      but positioned absolutely relative to this fixed wrapper.
  */}
        <AnimatePresence>
          {isPenMenuOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10, x: 0 }}
              animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className={`
        absolute z-50 bg-[#FCF8F8] dark:bg-[#1a1111] p-3 rounded-[1.5rem] shadow-2xl border border-[#452829]/10 dark:border-[#F5AFAF]/20
        bottom-[110%] left-0 sm:w-64 w-full
        sm:bottom-auto sm:top-0 sm:left-[110%] sm:ml-4
      `}
            >

              <div className="relative z-10 px-4 py-4 sm:w-auto bg-white/95 dark:bg-[#1a1111]/95 backdrop-blur-xl rounded-2xl border border-[#452829]/10 dark:border-[#F5AFAF]/10 shadow-xl selection:none">

                {/* === PEN SETTINGS CONTENT === */}
                {tool === 'pen' && (
                  <div className="space-y-6 animate-in fade-in zoom-in-95 duration-200">
                    {/* Ink Color Section */}
                    <section>
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#452829]/40 dark:text-[#FBEFEF]/40 mb-3 block">Ink Color</span>
                      <div className="flex flex-wrap gap-2.5">
                        {COLORS.map(c => (
                          <button
                            key={c}
                            onClick={() => setStrokeColor(c)}
                            className={`group relative w-7 h-7 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 ${strokeColor === c
                              ? 'ring-2 ring-offset-2 ring-[#452829] dark:ring-[#FBEFEF] shadow-md'
                              : 'hover:ring-2 hover:ring-[#452829]/20 dark:hover:ring-[#FBEFEF]/20'
                              }`}
                            style={{ backgroundColor: c }}
                          >
                            {strokeColor === c && <div className="absolute inset-0 rounded-full border-2 border-white/20 dark:border-black/20" />}
                          </button>
                        ))}
                        {/* Custom Picker */}
                        <label
                          className="group relative w-7 h-7 rounded-full bg-white dark:bg-[#2d1f1f] flex items-center justify-center cursor-pointer hover:scale-110 transition-all border-2 border-[#452829]/10 dark:border-[#FBEFEF]/10 shadow-sm active:scale-95"
                          style={{ borderColor: strokeColor }}
                        >
                          <svg className="w-3.5 h-3.5 text-[#452829]/40 dark:text-[#FBEFEF]/40 group-hover:text-[#452829] dark:group-hover:text-[#FBEFEF] transition-colors" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <input type="color" value={strokeColor} onChange={(e) => setStrokeColor(e.target.value)} className="opacity-0 absolute inset-0 cursor-pointer scale-150" />
                        </label>
                      </div>
                    </section>

                    {/* Thickness Section */}
                    <section>
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#452829]/40 dark:text-[#FBEFEF]/40 mb-3 block">Brush Size</span>
                      <div className="flex bg-[#452829]/5 dark:bg-[#FBEFEF]/5 p-1 rounded-xl border border-[#452829]/5 dark:border-[#FBEFEF]/5">
                        {WIDTHS.map(w => (
                          <button
                            key={w}
                            onClick={() => setStrokeWidth(w)}
                            className={`relative flex-1 h-9 rounded-lg flex items-center justify-center transition-all duration-200 ${strokeWidth === w
                              ? 'bg-white dark:bg-[#2d1f1f] shadow-sm text-[#452829] dark:text-[#FBEFEF]'
                              : 'text-[#452829]/40 dark:text-[#FBEFEF]/40 hover:text-[#452829] dark:hover:text-[#FBEFEF]'
                              }`}
                          >
                            <div className="bg-current rounded-full" style={{ width: Math.min(18, w * 1.5 + 2), height: Math.min(18, w * 1.5 + 2) }} />
                          </button>
                        ))}
                      </div>
                    </section>
                  </div>
                )}

                {/* === SHAPE SETTINGS CONTENT === */}
                {tool === 'shape' && (
                  <div className="space-y-6 animate-in fade-in zoom-in-95 duration-200">
                    {/* Outline Color */}
                    <section>
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#452829]/40 dark:text-[#FBEFEF]/40 mb-3 block">Outline Color</span>
                      <div className="flex flex-wrap gap-2.5">
                        {COLORS.map(c => (
                          <button
                            key={c}
                            onClick={() => setStrokeColor(c)}
                            className={`group relative w-7 h-7 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 ${strokeColor === c
                              ? 'ring-2 ring-offset-2 ring-[#452829] dark:ring-[#FBEFEF] shadow-md'
                              : 'hover:ring-2 hover:ring-[#452829]/20 dark:hover:ring-[#FBEFEF]/20'
                              }`}
                            style={{ backgroundColor: c }}
                          >
                            {strokeColor === c && <div className="absolute inset-0 rounded-full border-2 border-white/20 dark:border-black/20" />}
                          </button>
                        ))}
                        <label
                          className="group relative w-7 h-7 rounded-full bg-white dark:bg-[#2d1f1f] flex items-center justify-center cursor-pointer hover:scale-110 transition-all border-2 border-[#452829]/10 dark:border-[#FBEFEF]/10 shadow-sm active:scale-95"
                          style={{ borderColor: strokeColor }}
                        >
                          <svg className="w-3.5 h-3.5 text-[#452829]/40 dark:text-[#FBEFEF]/40 group-hover:text-[#452829] dark:group-hover:text-[#FBEFEF] transition-colors" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <input type="color" value={strokeColor} onChange={(e) => setStrokeColor(e.target.value)} className="opacity-0 absolute inset-0 cursor-pointer scale-150" />
                        </label>
                      </div>
                    </section>

                    {/* Border Weight */}
                    <section>
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#452829]/40 dark:text-[#FBEFEF]/40 mb-3 block">Border Weight</span>
                      <div className="flex bg-[#452829]/5 dark:bg-[#FBEFEF]/5 p-1 rounded-xl border border-[#452829]/5 dark:border-[#FBEFEF]/5">
                        {WIDTHS.map(w => (
                          <button
                            key={w}
                            onClick={() => setStrokeWidth(w)}
                            className={`relative flex-1 h-9 rounded-lg flex items-center justify-center transition-all duration-200 ${strokeWidth === w
                              ? 'bg-white dark:bg-[#2d1f1f] shadow-sm text-[#452829] dark:text-[#FBEFEF]'
                              : 'text-[#452829]/40 dark:text-[#FBEFEF]/40 hover:text-[#452829] dark:hover:text-[#FBEFEF]'
                              }`}
                          >
                            <div className="bg-current rounded-full w-7" style={{ height: Math.max(1.5, w / 2) }} />
                          </button>
                        ))}
                      </div>
                    </section>

                    {/* Fill Style */}
                    {['rect', 'ellipse', 'pentagon'].includes(shapeKind) && (
                      <section className="pt-4 border-t border-[#452829]/5 dark:border-[#FBEFEF]/5">
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#452829]/40 dark:text-[#FBEFEF]/40 mb-3 block">Fill Style</span>
                        <div className="flex flex-wrap gap-2.5">
                          {/* Transparent Toggle */}
                          <button
                            onClick={() => setFillColor('transparent')}
                            className={`group relative w-7 h-7 rounded-full border-2 transition-all flex items-center justify-center overflow-hidden ${fillColor === 'transparent'
                              ? 'border-[#452829] dark:border-[#FBEFEF] bg-[#452829]/5 dark:bg-[#FBEFEF]/10 ring-2 ring-offset-2 ring-[#452829]/20 dark:ring-[#FBEFEF]/20'
                              : 'border-[#452829]/20 dark:border-[#FBEFEF]/20 hover:border-[#452829]/40 dark:hover:border-[#FBEFEF]/40'
                              }`}
                          >
                            <div className="absolute inset-0 bg-white dark:bg-[#1a1111]" />
                            <div className="w-full h-[1.5px] bg-red-500/60 rotate-45 z-10" />
                          </button>

                          {/* Standard Swatches */}
                          {COLORS.map(c => (
                            <button
                              key={c}
                              onClick={() => setFillColor(c)}
                              className={`group relative w-7 h-7 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 ${fillColor === c
                                ? 'ring-2 ring-offset-2 ring-[#452829] dark:ring-[#FBEFEF] shadow-md'
                                : 'hover:ring-2 hover:ring-[#452829]/20 dark:hover:ring-[#FBEFEF]/20'
                                }`}
                              style={{ backgroundColor: c }}
                            >
                              {fillColor === c && <div className="absolute inset-0 rounded-full border-2 border-white/20 dark:border-black/20" />}
                            </button>
                          ))}

                          {/* Custom Fill Picker */}
                          <label
                            className="group relative w-7 h-7 rounded-full bg-white dark:bg-[#2d1f1f] flex items-center justify-center cursor-pointer hover:scale-110 transition-all border-2 shadow-sm active:scale-95"
                            style={{ borderColor: fillColor === 'transparent' ? (document.documentElement.classList.contains('dark') ? '#333' : '#e4e4e7') : fillColor }}
                          >
                            <svg className="w-3.5 h-3.5 text-[#452829]/40 dark:text-[#FBEFEF]/40 group-hover:text-[#452829] dark:group-hover:text-[#FBEFEF] transition-colors" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                              <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <input type="color" value={fillColor === 'transparent' ? '#ffffff' : fillColor} onChange={(e) => setFillColor(e.target.value)} className="opacity-0 absolute inset-0 cursor-pointer scale-150" />
                          </label>
                        </div>
                      </section>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>


        {/* 2. SCROLLABLE TOOL DOCK 
      This contains only the buttons. Because the menu is above this div (in the DOM), 
      overflow-x-auto here won't clip the menu.
  */}
        <div className="pointer-events-auto w-full flex 
    /* Mobile: Row with Scroll */
    flex-row overflow-x-auto no-scrollbar items-center px-4 py-3 gap-3 
    /* Desktop: Column */
    sm:flex-col sm:w-16 sm:p-2 sm:gap-4 sm:overflow-visible
    /* Colors: Light Mode = White/Cream, Dark Mode = Deep Cocoa */
    bg-[#FCF8F8] dark:bg-[#2d1f1f] 
    text-[#452829] dark:text-[#FBEFEF] 
    rounded-3xl shadow-2xl shadow-[#452829]/10 dark:shadow-[#452829]/30 
    border border-[#452829]/10 dark:border-[#FBEFEF]/5"
        >

          {/* PRIMARY TOOLS */}
          <div className="flex sm:flex-col gap-2 shrink-0">

            {/* Select Tool */}
            <button
              onClick={() => { setTool('select'); setIsPenMenuOpen(false); }}
              className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-2xl transition-all duration-300 ${tool === 'select'
                ? 'bg-[#F5AFAF] text-[#452829] shadow-lg scale-110'
                : 'text-[#452829] dark:text-[#FBEFEF] hover:bg-[#452829]/5 dark:hover:bg-[#FBEFEF]/10'
                }`}
            >
              {Icons.Select}
            </button>

            {/* Pen Tool */}
            <button
              onClick={() => {
                if (tool === 'pen') setIsPenMenuOpen(!isPenMenuOpen);
                setTool('pen');
                if (tool !== 'pen') setIsPenMenuOpen(false);
              }}
              className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-2xl transition-all duration-300 relative ${tool === 'pen'
                ? 'bg-[#F5AFAF] text-[#452829] shadow-[0_0_15px_rgba(245,175,175,0.4)] scale-110'
                : 'text-[#452829] dark:text-[#FBEFEF] hover:bg-[#452829]/5 dark:hover:bg-[#FBEFEF]/10'
                }`}
            >
              {Icons.Pen}
              {tool === 'pen' && (
                <span className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-[#FCF8F8] dark:border-[#2d1f1f]" style={{ backgroundColor: strokeColor }}></span>
              )}
            </button>

            {/* Eraser Tool */}
            <button
              onClick={() => { setTool('eraser'); setIsPenMenuOpen(false); }}
              className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-2xl transition-all duration-300 ${tool === 'eraser'
                ? 'bg-[#452829] text-[#FBEFEF] dark:bg-[#FBEFEF] dark:text-[#ef4444] shadow-lg scale-110'
                : 'text-[#452829] dark:text-[#FBEFEF] hover:bg-[#452829]/5 dark:hover:bg-[#FBEFEF]/10'
                }`}
            >
              {Icons.Eraser}
            </button>

            {/* Hand Tool */}
            <button
              onClick={() => { setTool('pan'); setIsPenMenuOpen(false); }}
              className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-2xl transition-all duration-300 ${tool === 'pan'
                ? 'bg-[#F5AFAF] text-[#452829] shadow-lg scale-110'
                : 'text-[#452829] dark:text-[#FBEFEF] hover:bg-[#452829]/5 dark:hover:bg-[#FBEFEF]/10'
                }`}
            >
              {Icons.Hand}
            </button>
          </div>

          {/* DIVIDER */}
          <div className="w-px h-8 bg-[#452829]/10 dark:bg-[#FBEFEF]/20 sm:w-8 sm:h-px shrink-0"></div>

          {/* SHAPE TOOLS */}
          <div className="flex sm:flex-col gap-2 shrink-0">
            {[
              { kind: 'line', icon: Icons.Line45 },
              { kind: 'arrow', icon: Icons.Arrow45 },
              { kind: 'rect', icon: Icons.Rect },
              { kind: 'ellipse', icon: Icons.Circle },
              { kind: 'pentagon', icon: Icons.Pentagon }
            ].map((shape) => (
              <button
                key={shape.kind}
                onClick={() => {
                  if (tool === 'shape' && shapeKind === shape.kind) {
                    setIsPenMenuOpen(!isPenMenuOpen);
                  } else {
                    setTool('shape');
                    setShapeKind(shape.kind);
                    setIsPenMenuOpen(false);
                  }
                }}
                className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-2xl transition-all duration-300 ${tool === 'shape' && shapeKind === shape.kind
                  ? 'bg-[#F5AFAF] text-[#452829] shadow-lg scale-110'
                  : 'text-[#452829] dark:text-[#FBEFEF] hover:bg-[#452829]/5 dark:hover:bg-[#FBEFEF]/10'
                  }`}
              >
                {shape.icon}
              </button>
            ))}
          </div>

        </div>

      </div>}


      {/* =========================================================================
        OBJECT PROPERTIES MENU (When an object is selected)
       ========================================================================= */}
      <AnimatePresence>
        {selectedGroupId && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: "-50%", scale: 0.95 }}
            animate={{ opacity: 1, y: 0, x: "-50%", scale: 1 }}
            exit={{ opacity: 0, y: -10, x: "-50%", scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed z-50 top-20 left-1/2 bg-[#FCF8F8] dark:bg-[#1a1111] rounded-2xl shadow-2xl shadow-[#452829]/20 dark:shadow-black/50 border border-[#452829]/10 dark:border-[#F5AFAF]/20 flex items-center w-[95vw] sm:w-auto max-w-2xl overflow-hidden pr-2"
          >

            {/* =======================
          LEFT SIDE: SCROLLABLE TOOLS
         ======================= */}
            <div className="flex-1 flex items-center overflow-x-auto no-scrollbar py-2 pl-4 pr-2 gap-4">

              {/* GROUP 1: STROKE COLOR */}
              <div className="flex flex-col gap-1 shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#452829]/40 dark:text-[#FBEFEF]/40 pl-1">Color</span>
                <div className="flex items-center -space-x-2 sm:hover:space-x-1 transition-all duration-300 px-1">
                  {COLORS.slice(0, 5).map(c => (
                    <button
                      key={c}
                      onClick={() => updateSelectedMeta({ ...selectedMeta, strokeColor: c })}
                      className={`w-7 h-7 rounded-full border-2 border-[#FCF8F8] dark:border-[#1a1111] sm:hover:scale-110 sm:hover:z-10 transition-transform relative ${selectedMeta?.strokeColor === c
                        ? 'z-20 ring-2 ring-[#452829] dark:ring-[#FBEFEF]'
                        : ''
                        }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                  <label className="w-7 h-7 rounded-full border-2 border-[#FCF8F8] dark:border-[#1a1111] flex items-center justify-center cursor-pointer bg-gradient-to-br from-[#F5AFAF] to-[#452829] sm:hover:scale-110 sm:hover:z-10 transition-transform relative z-0">
                    <span className="text-white text-[10px] font-bold">+</span>
                    <input
                      type="color"
                      className="opacity-0 absolute inset-0 cursor-pointer"
                      value={selectedMeta?.strokeColor || '#000000'}
                      onChange={(e) => updateSelectedMeta({ ...selectedMeta, strokeColor: e.target.value })}
                    />
                  </label>
                </div>
              </div>

              <div className="w-px h-8 bg-[#452829]/10 dark:bg-[#FBEFEF]/10 shrink-0"></div>

              {/* GROUP 2: THICKNESS */}
              <div className="flex flex-col gap-1 shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#452829]/40 dark:text-[#FBEFEF]/40 pl-1">Size</span>
                <div className="flex bg-[#452829]/5 dark:bg-[#FBEFEF]/5 p-1 rounded-xl border border-[#452829]/5 dark:border-[#FBEFEF]/5">
                  {WIDTHS.map(w => (
                    <button
                      key={w}
                      onClick={() => updateSelectedMeta({ ...selectedMeta, strokeWidth: w })}
                      className={`relative w-8 h-6 rounded-lg flex items-center justify-center transition-all duration-200 ${(selectedMeta?.strokeWidth || strokeWidth) === w
                        ? 'bg-white dark:bg-[#2d1f1f] shadow-sm text-[#452829] dark:text-[#FBEFEF]'
                        : 'text-[#452829]/30 dark:text-[#FBEFEF]/30 sm:hover:bg-white/50 dark:sm:hover:bg-[#FBEFEF]/10'
                        }`}
                    >
                      <div
                        className="bg-current rounded-full"
                        style={{ width: Math.min(14, w * 1.5 + 2), height: Math.min(14, w * 1.5 + 2) }}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* GROUP 3: FILL (Conditional) */}
              {(selectedMeta?.shapeType === 'rect' || selectedMeta?.shapeType === 'ellipse' || selectedMeta?.shapeType === 'pentagon') && (
                <>
                  <div className="w-px h-8 bg-[#452829]/10 dark:bg-[#FBEFEF]/10 shrink-0"></div>

                  <div className="flex flex-col gap-1 shrink-0">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#452829]/40 dark:text-[#FBEFEF]/40 pl-1">Fill</span>
                    <div className="flex items-center gap-2">
                      {/* No Fill Button */}
                      <button
                        onClick={() => updateSelectedMeta({ ...selectedMeta, fill: 'transparent' })}
                        className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all sm:hover:scale-105 ${!selectedMeta?.fill || selectedMeta?.fill === 'transparent'
                          ? 'border-[#452829] dark:border-[#FBEFEF] bg-[#452829]/5 dark:bg-[#FBEFEF]/10'
                          : 'border-[#452829]/20 dark:border-[#FBEFEF]/20'
                          }`}
                        title="No Fill"
                      >
                        <div className="w-full h-0.5 bg-red-400 dark:bg-red-400 rotate-45 transform scale-75"></div>
                      </button>

                      <div className="flex items-center -space-x-2 sm:hover:space-x-1 transition-all duration-300">
                        {COLORS.slice(0, 3).map(c => (
                          <button
                            key={c}
                            onClick={() => updateSelectedMeta({ ...selectedMeta, fill: c })}
                            className={`w-7 h-7 rounded-full border-2 border-[#FCF8F8] dark:border-[#1a1111] sm:hover:scale-110 sm:hover:z-10 transition-transform relative ${selectedMeta?.fill === c
                              ? 'z-20 ring-2 ring-[#452829] dark:ring-[#FBEFEF]'
                              : ''
                              }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                        <label className="w-7 h-7 rounded-full border-2 border-[#FCF8F8] dark:border-[#1a1111] flex items-center justify-center cursor-pointer bg-gradient-to-br from-[#F5AFAF] to-[#452829] sm:hover:scale-110 sm:hover:z-10 transition-transform relative z-0">
                          <span className="text-white text-[10px] font-bold">+</span>
                          <input
                            type="color"
                            className="opacity-0 absolute inset-0 cursor-pointer"
                            value={selectedMeta?.fill !== 'transparent' ? selectedMeta?.fill : '#ffffff'}
                            onChange={(e) => updateSelectedMeta({ ...selectedMeta, fill: e.target.value })}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* =======================
          RIGHT SIDE: FIXED ACTIONS
         ======================= */}
            <div className="shrink-0 flex items-center gap-2 pl-2 border-l border-[#452829]/10 dark:border-[#FBEFEF]/10 py-2">
              <button
                onClick={() => { socketRef.current.emit('erase', { groupIds: [selectedGroupId], userId: data.user.id }); setStrokes(prev => prev.filter(s => s.groupId !== selectedGroupId)); setSelectedGroupId(null); }}
                className="w-9 h-9 flex items-center justify-center bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-xl sm:hover:bg-red-100 dark:sm:hover:bg-red-900/40 transition-colors"
                title="Delete Shape"
              >
                {Icons.Trash}
              </button>
              <button
                onClick={() => { setSelectedGroupId(null); setSelectedMeta(null); }}
                className="w-9 h-9 flex items-center justify-center text-[#452829] dark:text-[#FBEFEF] hover:bg-[#FBEFEF] dark:hover:bg-[#2d1f1f] rounded-xl transition-colors"
                title="Deselect"
              >
                {Icons.Close}
              </button>
            </div>

          </motion.div>
        )}
      </AnimatePresence>

      {/* =========================================================================
    ADMIN MODAL (Clean, iOS-Settings Style)
   ========================================================================= */}
      <AnimatePresence>
        {showAdminModal && isAdmin && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#452829]/20 dark:bg-black/60 backdrop-blur-md">
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              className="bg-[#FCF8F8] dark:bg-[#1a1111] w-full max-w-2xl rounded-[2rem] shadow-2xl border border-[#452829]/10 dark:border-[#F5AFAF]/10 flex flex-col max-h-[80vh] overflow-hidden"
            >

              {/* Header */}
              <div className="px-6 py-4 border-b border-[#452829]/5 dark:border-[#FBEFEF]/5 flex justify-between items-center bg-white/50 dark:bg-[#2d1f1f]/30 backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-bold text-[#452829] dark:text-[#FBEFEF]">Session Control</h2>
                  <span className="bg-[#F5AFAF] text-[#452829] text-[10px] font-extrabold px-2 py-0.5 rounded-md tracking-wider">ADMIN</span>
                </div>
                <button
                  onClick={() => setShowAdminModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#452829]/5 dark:hover:bg-[#FBEFEF]/10 text-[#452829] dark:text-[#FBEFEF] transition-colors"
                >
                  {Icons.Close}
                </button>
              </div>

              {/* Tabs (Segmented Control) */}
              <div className="px-6 pt-4 pb-2">
                <div className="flex p-1 bg-[#452829]/5 dark:bg-[#2d1f1f] rounded-xl border border-[#452829]/5 dark:border-[#FBEFEF]/5">
                  {['users', 'settings', 'privacy'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setAdminTab(tab)}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${adminTab === tab
                        ? 'bg-white dark:bg-[#452829] text-[#452829] dark:text-[#FBEFEF] shadow-sm'
                        : 'text-[#452829]/40 dark:text-[#FBEFEF]/40 hover:text-[#452829]/70 dark:hover:text-[#FBEFEF]/80'
                        }`}
                    >
                      {tab} {tab === 'users' && `(${onlineUsers.length})`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto custom-scrollbar bg-[#FCF8F8] dark:bg-[#1a1111]">

                {/* USERS TAB */}
                {adminTab === 'users' && (
                  <div className="space-y-2">
                    {onlineUsers.map(user => (
                      <div key={user.userId} className="flex items-center justify-between p-3 bg-white dark:bg-[#2d1f1f] rounded-xl border border-[#452829]/5 dark:border-[#FBEFEF]/5">
                        <div className="flex items-center gap-3">
                          <img src={user.profile_p} className="w-9 h-9 rounded-full bg-[#FBEFEF] dark:bg-[#452829] object-cover border border-[#452829]/5 dark:border-[#FBEFEF]/10" />
                          <div>
                            <div className="text-sm font-bold text-[#452829] dark:text-[#FBEFEF]">{user.username}</div>
                            <div className="text-[10px] text-[#452829]/40 dark:text-[#FBEFEF]/40 font-mono">{user.userId.slice(0, 6)}...</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {boardSettings.canDraw && <button
                            onClick={() => handleToggleUserDraw(user.userId, user.username, user.canDraw)}
                            className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors border ${user.canDraw
                              ? 'bg-[#FBEFEF] dark:bg-[#452829] text-[#452829] dark:text-[#FBEFEF] border-[#452829]/10 dark:border-[#FBEFEF]/10'
                              : 'bg-[#452829] dark:bg-[#FBEFEF] text-[#FBEFEF] dark:text-[#452829] border-transparent'
                              }`}
                          >
                            {user.canDraw ? 'Draw: ON' : 'Draw: OFF'}
                          </button>}
                          {user.userId !== data.user.id && (
                            <div className="flex gap-1 ml-1 border-l border-[#452829]/10 pl-1">
                              <button onClick={() => handleKickUser(user.userId, user.username)} className="p-1.5 text-[#452829]/40 dark:text-[#FBEFEF]/40 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">{Icons.Kick}</button>
                              <button onClick={() => handleBanUser(user.userId, user.username)} className="p-1.5 dark:hover:bg-red-900/20 hover:bg-red-50 rounded-lg text-red-500">{Icons.Ban}</button>
                            </div>

                          )}
                        </div>
                      </div>
                    ))}
                    <button onClick={handleClearBoard} className="w-full mt-4 py-3 border border-red-200 dark:border-red-900/30 text-red-500 dark:text-red-400 rounded-xl text-sm font-bold hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      Reset Board
                    </button>
                  </div>
                )}

                {/* SETTINGS TAB */}
                {adminTab === 'settings' && (
                  <div className="space-y-6">
                    {/* Toggles */}
                    <div className="space-y-2">
                      {[
                        { label: 'Allow Drawing', desc: 'Enable drawing for everyone', val: boardSettings.canDraw, fn: () => setBoardSettings(p => ({ ...p, canDraw: !p.canDraw })) },
                        { label: 'Strict Eraser', desc: 'Users can only erase their own art', val: boardSettings.eraseOwnOnly, fn: () => setBoardSettings(p => ({ ...p, eraseOwnOnly: !p.eraseOwnOnly })) }
                      ].map((item, i) => (
                        <div key={i} onClick={item.fn} className="flex items-center justify-between p-3 bg-white dark:bg-[#2d1f1f] rounded-xl border border-[#452829]/5 dark:border-[#FBEFEF]/5 cursor-pointer hover:border-[#452829]/20 dark:hover:border-[#FBEFEF]/20 transition-all">
                          <div>
                            <div className="text-sm font-bold text-[#452829] dark:text-[#FBEFEF]">{item.label}</div>
                            <div className="text-[10px] text-[#452829]/40 dark:text-[#FBEFEF]/40">{item.desc}</div>
                          </div>
                          <div className={`w-10 h-6 rounded-full relative transition-colors ${item.val ? 'bg-[#F5AFAF]' : 'bg-[#452829]/10 dark:bg-[#FBEFEF]/10'}`}>
                            <div className={`absolute top-1 left-1 w-4 h-4 bg-white dark:bg-[#1a1111] rounded-full shadow-sm transition-transform ${item.val ? 'translate-x-4' : 'translate-x-0'}`} />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Slider */}
                    <div className="p-4 bg-white dark:bg-[#2d1f1f] rounded-xl border border-[#452829]/5 dark:border-[#FBEFEF]/5">
                      <div className="flex justify-between mb-3 text-xs font-bold text-[#452829]/60 dark:text-[#FBEFEF]/60">
                        <span>Max Users</span>
                        <span>{boardSettings.maxUsers}</span>
                      </div>
                      <input type="range" min="1" max="50" value={boardSettings.maxUsers} onChange={(e) => setBoardSettings(prev => ({ ...prev, maxUsers: parseInt(e.target.value) }))} className="w-full h-1.5 bg-[#452829]/10 dark:bg-[#FBEFEF]/10 rounded-lg accent-[#452829] dark:accent-[#F5AFAF]" />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => { socketRef.current.emit('admin-update-settings', { settings: { canDraw: true }, adminUserId: data.user.id }); }} className="py-3 bg-[#FBEFEF] dark:bg-[#2d1f1f] text-[#452829] dark:text-[#F5AFAF] border border-[#452829]/5 dark:border-[#FBEFEF]/10 rounded-xl text-xs font-bold hover:bg-[#F5AFAF] dark:hover:bg-[#3d2b2b]">Unlock All</button>
                      <button onClick={() => { socketRef.current.emit('admin-update-settings', { settings: { canDraw: false }, adminUserId: data.user.id }); }} className="py-3 bg-[#452829]/5 dark:bg-[#FBEFEF]/5 text-[#452829] dark:text-[#FBEFEF] rounded-xl text-xs font-bold hover:bg-[#452829]/10 dark:hover:bg-[#FBEFEF]/10">Lock All</button>
                    </div>

                    <button onClick={handleUpdateSettings} className="w-full py-3 bg-[#452829] dark:bg-[#FBEFEF] text-[#FBEFEF] dark:text-[#452829] rounded-xl text-sm font-bold hover:bg-[#2a1818] dark:hover:bg-white shadow-lg shadow-[#452829]/20 dark:shadow-[#FBEFEF]/10">Save Changes</button>
                  </div>
                )}

                {/* PRIVACY TAB */}
                {adminTab === 'privacy' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-[#F5AFAF]/10 dark:bg-[#F5AFAF]/5 rounded-2xl border border-[#F5AFAF]/20 dark:border-[#F5AFAF]/10">
                      <div className="flex items-center gap-3">
                        <div className="text-xl text-[#452829] dark:text-[#F5AFAF]">{boardSettings.isPrivate ? Icons.Lock : Icons.Unlock}</div>
                        <div>
                          <div className="text-sm font-bold text-[#452829] dark:text-[#FBEFEF]">{boardSettings.isPrivate ? 'Private' : 'Public'}</div>
                          <div className="text-[10px] text-[#452829]/50 dark:text-[#FBEFEF]/50">Invite-only access</div>
                        </div>
                      </div>
                      <button onClick={handleTogglePrivacy} className="px-4 py-2 bg-white dark:bg-[#2d1f1f] rounded-lg text-xs font-bold shadow-sm text-[#452829] dark:text-[#F9DFDF]">Toggle</button>
                    </div>

                    {boardSettings.isPrivate && (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input className="flex-1 px-3 py-2 bg-white dark:bg-[#2d1f1f] border border-[#452829]/10 dark:border-[#FBEFEF]/10 rounded-xl text-sm outline-none text-[#452829] dark:text-[#FBEFEF] placeholder:text-[#452829]/30 dark:placeholder:text-[#FBEFEF]/30" placeholder="User ID..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
                          <button onClick={handleAddAllowedUser} className="px-4 bg-[#452829] dark:bg-[#FBEFEF] text-[#FBEFEF] dark:text-[#452829] rounded-xl text-xs font-bold">Add</button>
                        </div>
                        <div className="bg-white dark:bg-[#2d1f1f] rounded-xl border border-[#452829]/5 dark:border-[#FBEFEF]/5 p-2 h-32 overflow-y-auto custom-scrollbar">
                          {allowedUsers.length === 0 ? <div className="text-center text-[10px] text-[#452829]/30 dark:text-[#FBEFEF]/30 py-4">No whitelist</div> : allowedUsers.map(u => (
                            <div key={u} className="flex justify-between p-2 hover:bg-[#FBEFEF] dark:hover:bg-[#1a1111] rounded-lg group transition-colors">
                              <span className="text-xs font-mono text-[#452829] dark:text-[#FBEFEF]">{u}</span>
                              <button onClick={() => handleRemoveAllowedUser(u)} className="text-red-400 dark:text-red-300 opacity-0 group-hover:opacity-100">{Icons.Close}</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* =========================================================================
    EXPORT MODAL (Dark Mode Optimized)
   ========================================================================= */}
      <AnimatePresence>
        {showDownloadModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#452829]/20 dark:bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              className="bg-[#FCF8F8] dark:bg-[#1a1111] w-full max-w-[320px] rounded-[2rem] shadow-2xl border border-[#452829]/10 dark:border-[#F5AFAF]/10 p-5"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-[#452829] dark:text-[#F9DFDF]">Export</h2>
                <button onClick={() => setShowDownloadModal(false)} className="text-[#452829]/40 dark:text-[#FBEFEF]/40 hover:text-[#452829] dark:hover:text-[#FBEFEF] transition-colors">{Icons.Close}</button>
              </div>

              <div className="space-y-4">
                {/* Format Grid */}
                <div className="grid grid-cols-3 gap-2">
                  {['png', 'jpg', 'svg'].map(f => (
                    <button
                      key={f}
                      onClick={() => setDownloadSettings({ ...downloadSettings, format: f })}
                      className={`py-2 rounded-xl text-xs font-bold uppercase transition-all border ${downloadSettings.format === f
                        ? 'bg-[#452829] dark:bg-[#FBEFEF] text-[#FBEFEF] dark:text-[#452829] border-transparent shadow-lg'
                        : 'bg-white dark:bg-[#2d1f1f] text-[#452829]/50 dark:text-[#FBEFEF]/50 border-[#452829]/5 dark:border-[#FBEFEF]/5 hover:bg-[#FBEFEF] dark:hover:bg-[#3d2b2b]'
                        }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>

                {/* Background Toggle */}
                <div className="flex p-1 bg-[#452829]/5 dark:bg-[#2d1f1f] rounded-xl border border-[#452829]/5 dark:border-[#FBEFEF]/5">
                  {['plain', 'patterned'].map(b => (
                    <button
                      key={b}
                      onClick={() => setDownloadSettings({ ...downloadSettings, background: b })}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold capitalize transition-all ${downloadSettings.background === b
                        ? 'bg-white dark:bg-[#452829] shadow-sm text-[#452829] dark:text-[#FBEFEF]'
                        : 'text-[#452829]/40 dark:text-[#FBEFEF]/40 hover:text-[#452829] dark:hover:text-[#FBEFEF]'
                        }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>

                {/* Quality Slider (Visual) */}
                {downloadSettings.format !== 'svg' && (
                  <div className="flex justify-between items-center px-1">
                    <span className="text-[10px] font-bold text-[#452829]/40 dark:text-[#FBEFEF]/40 uppercase">Quality</span>
                    <div className="flex gap-1">
                      {[0.5, 0.8, 1.0].map((q, i) => (
                        <button
                          key={q}
                          onClick={() => setDownloadSettings({ ...downloadSettings, quality: q })}
                          className={`w-8 h-1.5 rounded-full transition-all ${downloadSettings.quality >= q
                            ? 'bg-[#F5AFAF]'
                            : 'bg-[#452829]/10 dark:bg-[#FBEFEF]/10'
                            }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <button onClick={handleDownload} className="w-full py-3 bg-[#452829] dark:bg-[#FBEFEF] text-[#FBEFEF] dark:text-[#452829] rounded-xl font-bold text-sm hover:bg-[#2a1818] dark:hover:bg-white shadow-xl shadow-[#452829]/20 dark:shadow-[#FBEFEF]/10 transition-transform active:scale-95">
                  Download Image
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* =========================================================================
    SHARE MODAL (Dark Mode Optimized)
   ========================================================================= */}
      <AnimatePresence>
        {showShareModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#452829]/20 dark:bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              className="bg-[#FCF8F8] dark:bg-[#1a1111] w-full max-w-[320px] rounded-[2rem] shadow-2xl border border-[#452829]/10 dark:border-[#F5AFAF]/10 p-5 overflow-hidden relative"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#452829] to-[#F5AFAF]"></div>
              <button onClick={() => setShowShareModal(false)} className="absolute top-4 right-4 text-[#452829]/40 dark:text-[#FBEFEF]/40 hover:text-[#452829] dark:hover:text-[#FBEFEF] transition-colors">{Icons.Close}</button>

              <div className="mt-2 mb-6">
                <div className="w-10 h-10 bg-[#F5AFAF]/20 dark:bg-[#F5AFAF]/10 text-[#452829] dark:text-[#F5AFAF] rounded-xl flex items-center justify-center mb-3">
                  {Icons.Share}
                </div>
                <h2 className="text-lg font-bold text-[#452829] dark:text-[#F9DFDF]">Invite</h2>
                <p className="text-xs text-[#452829]/50 dark:text-[#FBEFEF]/50 leading-relaxed">Share this link to collaborate in real-time.</p>
              </div>

              <div className="space-y-3">
                {/* Copy Field */}
                <div className="flex items-center bg-white dark:bg-[#2d1f1f] border border-[#452829]/10 dark:border-[#FBEFEF]/10 rounded-xl p-1 pl-3 shadow-sm">
                  <div className="flex-1 truncate text-xs font-mono text-[#452829]/60 dark:text-[#FBEFEF]/60 select-all">
                    {window.location.href.replace(/^https?:\/\//, '')}
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${isCopied
                      ? 'bg-[#F5AFAF] text-[#452829]'
                      : 'bg-[#452829] dark:bg-[#FBEFEF] text-[#FBEFEF] dark:text-[#452829]'
                      }`}
                  >
                    {isCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>

                <button onClick={handleSystemShare} className="w-full py-2.5 border-2 border-[#452829]/5 dark:border-[#FBEFEF]/5 hover:border-[#452829]/20 dark:hover:border-[#FBEFEF]/20 text-[#452829] dark:text-[#F9DFDF] rounded-xl font-bold text-xs transition-colors flex items-center justify-center gap-2 bg-white dark:bg-[#2d1f1f]">
                  {Icons.Share} Send Invite
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* =========================================================================
        CANVAS AREA
       ========================================================================= */}

      <div
        ref={containerRef}
        className="flex-1 w-full relative overflow-hidden bg-[#f0f2f5] dark:bg-black touch-action-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onPointerCancel={handlePointerUp}
      >
        <div
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})`,
            transformOrigin: '0 0',
            width: BOARD_WIDTH,
            height: BOARD_HEIGHT
          }}
          className={`absolute bg-white dark:bg-white bg-graph-pattern shadow-2xl border-[3px] border-slate-300 dark:border-gray-700 ${cursorClass}`}
        >
          <svg
            width={BOARD_WIDTH}
            height={BOARD_HEIGHT}
            viewBox={`0 0 ${BOARD_WIDTH} ${BOARD_HEIGHT}`}
            className="w-full h-full pointer-events-none"
          >
            {RenderedStrokes}
            {selectedGroupId && selectedMeta && (
              <g className="pointer-events-auto">
                {/* Bounding Box */}
                <rect
                  x={selectedMeta.x0} y={selectedMeta.y0}
                  width={selectedMeta.width} height={selectedMeta.height}
                  fill="none" stroke="#3b82f6" strokeWidth={2 / view.z} strokeDasharray="4"
                />
                {/* Interactive Corner Handles */}
                {/* {[
                  { cx: selectedMeta.x0, cy: selectedMeta.y0, c: 'tl' },
                  { cx: selectedMeta.x0 + selectedMeta.width, cy: selectedMeta.y0, c: 'tr' },
                  { cx: selectedMeta.x0, cy: selectedMeta.y0 + selectedMeta.height, c: 'bl' },
                  { cx: selectedMeta.x0 + selectedMeta.width, cy: selectedMeta.y0 + selectedMeta.height, c: 'br' }
                ].map(h => (
                  <circle
                    key={h.c} cx={h.cx} cy={h.cy} r={6 / view.z}
                    fill="white" stroke="#3b82f6" strokeWidth={2 / view.z}
                    style={{ cursor: h.c === 'tl' || h.c === 'br' ? 'nwse-resize' : 'nesw-resize' }}
                  />
                ))} */}
              </g>
            )}
            {tool === 'pen' && currentPath.length > 0 && (
              <path
                d={currentPathString}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {tool === 'shape' && shapePreview && (() => {
              const s = shapePreview;
              const left = Math.min(s.x0, s.x1);
              const top = Math.min(s.y0, s.y1);
              const w = Math.abs(s.x1 - s.x0);
              const h = Math.abs(s.y1 - s.y0);
              if (shapeKind === 'rect') return <rect x={left} y={top} width={w} height={h} fill="none" stroke={strokeColor} strokeWidth={strokeWidth} opacity={0.7} strokeDasharray="4 3" />;
              if (shapeKind === 'ellipse') return <ellipse cx={left + w / 2} cy={top + h / 2} rx={Math.max(1, w / 2)} ry={Math.max(1, h / 2)} fill="none" stroke={strokeColor} strokeWidth={strokeWidth} opacity={0.7} strokeDasharray="4 3" />;
              if (shapeKind === 'pentagon') {
                const centerX = left + w / 2;
                const centerY = top + h / 2;
                const rx = w / 2;
                const ry = h / 2;

                // Calculate the 5 points of the pentagon
                const points = Array.from({ length: 5 }, (_, i) => {
                  // Subtract Math.PI / 2 (90 degrees) to make the pentagon point upwards
                  const angle = (i * 2 * Math.PI) / 5 - Math.PI / 2;
                  const x = centerX + rx * Math.cos(angle);
                  const y = centerY + ry * Math.sin(angle);
                  return `${x},${y}`;
                }).join(' ');

                return (
                  <polygon
                    points={points}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    opacity={0.7}
                    strokeDasharray="4 3"
                  />
                );
              }
              return <line x1={s.x0} y1={s.y0} x2={s.x1} y2={s.y1} stroke={strokeColor} strokeWidth={strokeWidth} opacity={0.7} strokeDasharray="4 3" />;
            })()}

            {Object.entries(cursors).map(([uName, cursor]) => (
              <g key={uName} transform={`translate(${cursor.x}, ${cursor.y})`} className="transition-transform duration-75">
                <path d="M0 0L14 14L6 16L0 22Z" fill="#4f46e5" stroke="white" strokeWidth="2" className="drop-shadow-sm" />
                <rect x="18" y="-5" width={uName.length * 10 + 16} height="28" rx="6" fill="#1e293b" />
                <text x="26" y="13" fill="white" fontSize="14" fontWeight="bold" fontFamily='"Permanent Marker", cursive'>
                  {uName}
                </text>
              </g>
            ))}

            {/* Selection overlay + resize handles */}
            {selectedGroupId && (() => {
              // determine bounds either from selectedMeta or by computing from strokes of the group
              const group = strokes.filter(s => s.groupId === selectedGroupId);
              if (!group || group.length === 0) return null;
              let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;

              // if we have a selectedMeta (shape) prefer it
              const meta = selectedMeta || group[0].meta || null;
              if (meta && (meta.x0 != null || meta.x != null)) {
                const x0 = meta.x0 ?? meta.x ?? 0;
                const y0 = meta.y0 ?? meta.y ?? 0;
                const x1 = meta.x1 ?? (meta.x != null ? meta.x + 1 : null) ?? x0;
                const y1 = meta.y1 ?? (meta.y != null ? meta.y + 1 : null) ?? y0;
                left = Math.min(x0, x1);
                top = Math.min(y0, y1);
                right = Math.max(x0, x1);
                bottom = Math.max(y0, y1);
              } else {
                // compute bounds from group's strokes (legacy lines and shapes)
                group.forEach(s => {
                  const m = s.meta || {};
                  const sx0 = m.x0 ?? s.x0;
                  const sy0 = m.y0 ?? s.y0;
                  const sx1 = m.x1 ?? s.x1;
                  const sy1 = m.y1 ?? s.y1;
                  if (typeof sx0 === 'number' && typeof sy0 === 'number') {
                    left = Math.min(left, sx0);
                    top = Math.min(top, sy0);
                    right = Math.max(right, sx0);
                    bottom = Math.max(bottom, sy0);
                  }
                  if (typeof sx1 === 'number' && typeof sy1 === 'number') {
                    left = Math.min(left, sx1);
                    top = Math.min(top, sy1);
                    right = Math.max(right, sx1);
                    bottom = Math.max(bottom, sy1);
                  }
                });
                if (left === Infinity) return null;
              }

              const w = Math.max(4, right - left);
              const h = Math.max(4, bottom - top);
              const r = 6 / (view.z || 1);
              return (
                <g style={{ pointerEvents: 'all' }}>
                  <rect x={left} y={top} width={w} height={h} fill="none" stroke="#2563eb" strokeWidth={2 / (view.z || 1)} strokeDasharray="4 3" />
                  <circle cx={left} cy={top} r={r} fill="#fff" stroke="#2563eb" strokeWidth={1} style={{ pointerEvents: 'all', cursor: 'nwse-resize' }} onPointerDown={(e) => { e.stopPropagation(); const p = getBoardCoords(e.clientX, e.clientY); resizingRef.current = { corner: 'tl', start: p, prevMeta: selectedMeta || null }; resizeStartMetaRef.current = JSON.parse(JSON.stringify(selectedMeta || group[0].meta || {})); }} />
                  <circle cx={left + w} cy={top} r={r} fill="#fff" stroke="#2563eb" strokeWidth={1} style={{ pointerEvents: 'all', cursor: 'nesw-resize' }} onPointerDown={(e) => { e.stopPropagation(); const p = getBoardCoords(e.clientX, e.clientY); resizingRef.current = { corner: 'tr', start: p, prevMeta: selectedMeta || null }; resizeStartMetaRef.current = JSON.parse(JSON.stringify(selectedMeta || group[0].meta || {})); }} />
                  <circle cx={left} cy={top + h} r={r} fill="#fff" stroke="#2563eb" strokeWidth={1} style={{ pointerEvents: 'all', cursor: 'nesw-resize' }} onPointerDown={(e) => { e.stopPropagation(); const p = getBoardCoords(e.clientX, e.clientY); resizingRef.current = { corner: 'bl', start: p, prevMeta: selectedMeta || null }; resizeStartMetaRef.current = JSON.parse(JSON.stringify(selectedMeta || group[0].meta || {})); }} />
                  <circle cx={left + w} cy={top + h} r={r} fill="#fff" stroke="#2563eb" strokeWidth={1} style={{ pointerEvents: 'all', cursor: 'nwse-resize' }} onPointerDown={(e) => { e.stopPropagation(); const p = getBoardCoords(e.clientX, e.clientY); resizingRef.current = { corner: 'br', start: p, prevMeta: selectedMeta || null }; resizeStartMetaRef.current = JSON.parse(JSON.stringify(selectedMeta || group[0].meta || {})); }} />
                </g>
              );
            })()}

            {tool === 'eraser' && (
              <circle
                cx={cursorPos.x}
                cy={cursorPos.y}
                r={ERASER_RADIUS}
                fill="rgba(239, 68, 68, 0.2)"
                stroke="#ef4444"
                strokeWidth="2"
                strokeDasharray="4 2"
              />
            )}
          </svg>
          {textInputVisible && (
            <div style={{ position: 'absolute', left: textPosition.x, top: textPosition.y - textFontSize, transform: 'translate(0,0)' }}>
              <input
                autoFocus
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') finalizeText();
                  if (e.key === 'Escape') { setTextInputVisible(false); setTextValue(''); }
                }}
                onBlur={() => finalizeText()}
                style={{ fontSize: textFontSize, fontFamily: 'Permanent Marker, cursive', border: '2px solid #333', padding: '4px 6px', borderRadius: 6 }}
              />
            </div>
          )}
        </div>



      </div>


      {/* =========================================================================
        FLOATING CHAT / SIDE PANEL BUTTON
        Bottom Right (Desktop) / Above Toolbar (Mobile)
       ========================================================================= */}
      {/* <div className="absolute bottom-24 right-4 sm:bottom-6 z-40 flex flex-col items-end pointer-events-auto"> */}
      <div className="fixed z-40 
    /* Mobile: Bottom Dock Wrapper */
    bottom-24 sm:bottom-4 items-end right-4 flex flex-col
    ">

        <AnimatePresence>
          {isSidePanelOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="mb-4 w-[calc(100vw-32px)] max-w-xs sm:w-80 h-[50vh] sm:h-[500px] bg-[#FCF8F8] dark:bg-[#1a1111] rounded-[2rem] shadow-2xl border border-[#452829]/5 dark:border-[#F5AFAF]/10 flex flex-col overflow-hidden relative origin-bottom-right"
            >

              {/* === Header & Tabs === */}
              <div className="p-4 bg-[#FCF8F8]/95 dark:bg-[#1a1111]/95 backdrop-blur-md border-b border-[#452829]/5 z-10 shrink-0">
                <div className="flex p-1 bg-[#FBEFEF] dark:bg-[#2d1f1f] rounded-2xl">
                  <button
                    onClick={() => { setActiveTab('chat'); setUnreadCount(0); }}
                    className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all duration-300 ${activeTab === 'chat'
                      ? 'bg-[#452829] text-[#FCF8F8] shadow-md'
                      : 'text-[#452829]/50 dark:text-[#FBEFEF]/50 hover:bg-[#FCF8F8]/50'
                      }`}
                  >
                    Chat {unreadCount > 0 && <span className="ml-1 bg-[#F5AFAF] text-[#452829] px-1.5 py-0.5 rounded-full text-[10px]">{unreadCount}</span>}
                  </button>
                  <button
                    onClick={() => setActiveTab('users')}
                    className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all duration-300 ${activeTab === 'users'
                      ? 'bg-[#452829] text-[#FCF8F8] shadow-md'
                      : 'text-[#452829]/50 dark:text-[#FBEFEF]/50 hover:bg-[#FCF8F8]/50'
                      }`}
                  >
                    Artists <span className="ml-1 opacity-60 text-[10px]">{onlineUsers.length}</span>
                  </button>
                </div>
              </div>

              {/* === Content Area === */}
              <div className="flex-1 overflow-hidden relative bg-[#FCF8F8] dark:bg-[#1a1111]">

                {/* --- CHAT TAB --- */}
                {activeTab === 'chat' && (
                  <div className="flex flex-col h-full">
                    {/* Messages List */}
                    <div className="flex-1 p-4 flex flex-col gap-3 overflow-y-auto custom-scrollbar">
                      {chatMessages.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-full text-[#452829]/30 dark:text-[#FBEFEF]/30 space-y-2">
                          <div className="w-12 h-12 rounded-2xl bg-[#FBEFEF] dark:bg-[#2d1f1f] flex items-center justify-center text-2xl">
                            {Icons.Chat}
                          </div>
                          <p className="font-bold text-sm">Quiet in here...</p>
                        </div>
                      )}

                      {chatMessages.map((msg) => (
                        <div key={msg.id} className={`flex flex-col max-w-[85%] ${msg.isMe ? 'self-end items-end' : 'self-start items-start'}`}>
                          {!msg.isMe && (
                            <span className="text-[10px] font-bold text-[#452829]/50 dark:text-[#FBEFEF]/50 ml-2 mb-1">
                              {msg.sender}
                            </span>
                          )}
                          <div className={`px-4 py-2.5 text-sm break-words shadow-sm ${msg.isMe
                            ? 'bg-[#452829] text-[#FBEFEF] rounded-2xl rounded-tr-sm'
                            : 'bg-white dark:bg-[#2d1f1f] text-[#452829] dark:text-[#F9DFDF] border border-[#452829]/5 dark:border-[#FBEFEF]/5 rounded-2xl rounded-tl-sm'
                            }`}>
                            {msg.text}
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-3 bg-[#FCF8F8] dark:bg-[#1a1111] border-t border-[#452829]/5 dark:border-[#FBEFEF]/5">
                      <div className="flex gap-2 items-center bg-[#FBEFEF] dark:bg-[#2d1f1f] p-1.5 rounded-[1.25rem] border border-[#452829]/5 transition-colors focus-within:border-[#F5AFAF] focus-within:ring-2 focus-within:ring-[#F5AFAF]/20">
                        <input
                          className="flex-1 bg-transparent px-3 py-2 text-sm outline-none font-medium text-[#452829] dark:text-[#F9DFDF] placeholder:text-[#452829]/40"
                          placeholder="Type a message..."
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && sendChat()}
                        />
                        <button
                          onClick={sendChat}
                          disabled={!chatInput.trim()}
                          className={`p-2 rounded-xl transition-all ${chatInput.trim()
                            ? 'bg-[#452829] text-[#FBEFEF] shadow-md hover:scale-105 active:scale-95'
                            : 'bg-[#452829]/10 text-[#452829]/40 cursor-not-allowed'
                            }`}
                        >
                          {Icons.Send}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* --- ARTISTS TAB --- */}
                {activeTab === 'users' && (
                  <div className="p-4 overflow-y-auto h-full custom-scrollbar space-y-2">
                    {onlineUsers.map(user => (
                      <div key={user.id} className="flex items-center gap-3 p-3 bg-white dark:bg-[#2d1f1f] rounded-2xl border border-[#452829]/5 shadow-sm">
                        <div className="relative flex-shrink-0">
                          <img
                            src={user.profile_p}
                            alt=""
                            className="w-10 h-10 rounded-full bg-[#FBEFEF] object-cover border border-[#452829]/10"
                          />
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-[#2d1f1f]"></div>
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="text-sm font-bold text-[#452829] dark:text-[#F9DFDF] truncate">
                            {user.username}
                          </span>
                          <span className="text-[10px] font-bold text-[#452829]/40 uppercase tracking-wide">
                            {user.id === socketRef.current?.id ? 'You' : 'Artist'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* === Trigger Button (FAB) === */}
        <button
          onClick={() => {
            setIsSidePanelOpen(!isSidePanelOpen);
            if (!isSidePanelOpen) setUnreadCount(0);
          }}
          className={`
      group relative w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full shadow-lg border-4 border-[#FCF8F8] dark:border-[#1a1111] transition-all duration-300 hover:scale-110 active:scale-90
      ${isSidePanelOpen
              ? 'bg-[#FBEFEF] text-[#452829] rotate-90'
              : 'bg-[#452829] text-[#FBEFEF]'
            }
    `}
        >
          {isSidePanelOpen ? Icons.Close : Icons.Chat}

          {!isSidePanelOpen && unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#F5AFAF] text-[#452829] text-[10px] font-extrabold h-5 w-5 flex items-center justify-center rounded-full border-2 border-[#FCF8F8] dark:border-[#1a1111] animate-bounce">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* --- Utility: Theme Switcher & Info --- */}
      <div className="fixed top-20 sm:bottom-6 sm:top-auto sm:left-12 left-4 z-30 flex items-center gap-2 pointer-events-auto">
        <span className="text-xs font-bold text-[#452829]/30 dark:text-[#FBEFEF]/30 bg-[#FBEFEF]/50 dark:bg-[#2d1f1f]/50 px-2 py-1 rounded-lg">
          {Math.round(view.x)}, {Math.round(view.y)}
        </span>
      </div>

      {/* --- Admin Notifications --- */}
      <AnimatePresence>
        {adminMessage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            style={{
              x: "-50%",
            }}
            className="fixed top-16 left-1/2 z-[100]
             flex items-center gap-3 px-3 py-2 pr-4
             bg-[#F5AFAF]/95 dark:bg-[#F5AFAF]
             backdrop-blur-xl text-[#452829]
             rounded-full
             shadow-[0_20px_40px_-10px_rgba(69,40,41,0.3)]
             border border-white/20 dark:border-[#452829]/10
             ring-1 ring-[#452829]/5"
          >
            {/* Icon Badge */}
            <div className="w-8 h-8 flex items-center justify-center bg-white/30 dark:bg-[#452829]/10 rounded-full shrink-0">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>

            {/* Message */}
            <div className="flex flex-col">
              {/* <span className="text-[10px] uppercase font-extrabold opacity-60 tracking-wider leading-none mb-0.5">
          System
        </span> */}
              <span className="text-sm font-bold leading-none whitespace-nowrap">
                {adminMessage}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>


    </div>
  );
}
